import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, OnDestroy, Output, inject, HostListener, ElementRef, Renderer2, NgZone } from '@angular/core';
import { Subject, Subscription, debounceTime, skip } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from 'src/app/components/shared.module';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { FsBaseService } from 'src/app/services/fs-base.service';
import { MergeStatusService } from 'src/app/services/merge-status.service';
import { UtilService } from 'src/app/services/util.service';
import { ModelReferencePayload, ReferenceUtilService } from 'src/app/components/reference-tree-view/reference-util.service';
import { RepoManagementService } from '../repo-management.service';
import { BranchData, MergeChange, MergeAction, RecordChangeType, ComponentDetail, ComponentAdditionalInfo, ReviewMergeChange, GroupedReviewChange, MergeChangesRequest, MergeBranchRequest, MergeBranchComponentSet, MergeStatus, CONFLICT_MESSAGE_KEYS, AutoSelectRequest } from '../repo-management.types';
import { GlobalConstants } from 'src/app/base/global.constants';
import { environment } from 'src/environments/environment';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';

interface BranchOption {
  label: string;
  value: BranchData;
}

interface FilterOption {
  label: string;
  value: string;
}

@Component({
  selector: 'vb-merge-branch',
  templateUrl: './merge-branch.component.html',
  styleUrls: ['./merge-branch.component.scss'],
  standalone: true,
  imports: [CommonModule, SharedModule, RadioButtonModule, TranslateModule],
  providers: [ConfirmationService]
})
export class MergeBranchComponent implements OnInit, OnDestroy {
  // Suite merge flag and application filter
  isSuiteMerge = false;
  // Lookup map: appSid -> appName (built from branches data)
  private readonly appSidToNameMap = new Map<string, string>();
  // Step 3: Application grouping for suite merges
  applicationGroupedReviewChanges: Array<{
    applicationName: string;
    groupedReviewChanges: GroupedReviewChange[];
    expanded: boolean;
  }> = [];
  applicationOptions: FilterOption[] = [];
  filterApplications: string[] = [];
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly dialogConfig = inject(DynamicDialogConfig);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly repoService = inject(RepoManagementService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly mergeStatusService = inject(MergeStatusService);
  private readonly fsService = inject(FsBaseService);
  private readonly referenceUtilService = inject(ReferenceUtilService);
  readonly utilService = inject(UtilService);
  private readonly elRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly ngZone = inject(NgZone);

  activeIndex = 0;

  // Step 1: Branch selection
  sourceBranch: BranchData | null = null;
  targetBranch: BranchData | null = null;
  availableBranches: BranchOption[] = [];
  private isUserChangingTargetBranch = false; // Flag to skip Firestore handler when user changes target branch
  private isUserCancellingMerge = false; // Flag to skip Firestore handler when user cancels merge
  private initialTargetBranchId: string | null = null; // Track initial target branch to detect changes
  loadingBranches = false;
  targetBranchHasOngoingMerge = false; // Flag to indicate target branch has ongoing merge
  targetBranchOngoingMergeMessage = ''; // Error message for ongoing merge on target branch
  targetBranchDeleted = false; // Flag to indicate target branch was deleted externally

  // Step 2: Select Changes
  isProcessRunning = false;
  processWarningMessage = '';
  comparisonDate: string | number | Date | null = null;
  noMergesDone = false; // Flag to indicate if no merges have been done (showing created date instead)
  mergeHistoryTrackerId: string | null = null;
  private sourceBranchFromApi: BranchData | null = null;

  // All changes from API (unfiltered, unsorted)
  private allMergeChanges: MergeChange[] = [];
  // Filtered and sorted changes (full dataset)
  private filteredMergeChanges: MergeChange[] = [];
  // Displayed changes (paginated for infinite scroll)
  mergeChanges: MergeChange[] = [];
  loadingChanges = false;

  // Header checkbox states for bulk selection
  selectAllSource = true;
  selectAllTarget = false;
  selectAllDontMerge = false;

  get hasNoChanges(): boolean {
    return this.allMergeChanges.length === 0;
  }

  // Pagination for infinite scroll
  readonly PAGE_SIZE = 100;
  private currentPage = 0;
  hasMoreData = true;
  loadingMore = false;

  // Step 2: Filters and Sorting
  filterComponentTypes: string[] = [];
  filterChangeTypes: string[] = [];
  filterActionTypes: string[] = [];
  filterModifiedBy: string[] = [];
  filterConflicts: string[] = [];
  componentTypeOptions: FilterOption[] = [];
  modifiedByOptions: FilterOption[] = [];
  get changeTypeOptions(): FilterOption[] {
    return [
      { label: 'ADDED', value: 'ADDED' },
      { label: 'MODIFIED', value: 'MODIFIED' },
      { label: 'RENAMED', value: 'RENAMED' },
      { label: 'RENAMED & MODIFIED', value: 'RENAMED_AND_MODIFIED' },
      { label: 'DELETED', value: 'DELETED' }
    ];
  }
  get actionTypeOptions(): FilterOption[] {
    return [
      { label: this.sourceBranch?.refsName || this.translate.instant('MERGE_SOURCE'), value: 'MERGE_SOURCE' },
      { label: this.targetBranch?.refsName || this.translate.instant('MERGE_TARGET'), value: 'MERGE_TARGET' },
      { label: this.translate.instant('MERGE_LATER'), value: 'MERGE_LATER' }
    ];
  }
  get conflictOptions(): FilterOption[] {
    return [
      { label: this.translate.instant('YES'), value: 'YES' },
      { label: this.translate.instant('NO'), value: 'NO' }
    ];
  }
  sortField = 'componentType';
  sortOrder: 'asc' | 'desc' = 'asc';

  // Filter dialog
  showFilterDialog = false;
  tempFilterComponentTypes: string[] = [];
  tempFilterChangeTypes: string[] = [];
  tempFilterActionTypes: string[] = [];
  tempFilterConflicts: string[] = [];

  // Step 2: Actions
  bulkAction: MergeAction | null = 'MERGE_SOURCE';
  applyToScope: 'all' | 'filtered' = 'all';
  get actionOptions() {
    return [
      { label: this.translate.instant('MERGE_SOURCE'), value: 'MERGE_SOURCE' as MergeAction },
      { label: this.translate.instant('MERGE_TARGET'), value: 'MERGE_TARGET' as MergeAction },
      { label: this.translate.instant('MERGE_LATER'), value: 'MERGE_LATER' as MergeAction }
    ];
  }

  // Step 3: Review and Merge
  reviewChanges: ReviewMergeChange[] = [];
  filteredReviewChanges: ReviewMergeChange[] = [];
  loadingReviewChanges = false;
  reviewError = '';
  userSelectedComponentIds = new Set<string>();

  // Cache for API call payloads to avoid redundant calls
  private lastGetChangesPayload: { sourceBranchId: string; targetBranchId: string } | null = null;
  private lastAutoSelectPayload: string | null = null; // JSON stringified selectedComponents

  // Step 3: Grouped review data
  groupedReviewChanges: GroupedReviewChange[] = [];
  expandedGroups = new Set<string>();

  // Step 3: Filters
  reviewFilterApplications: string[] = [];
  reviewFilterComponentTypes: string[] = [];
  reviewFilterChangeTypes: string[] = [];
  reviewComponentTypeOptions: FilterOption[] = [];
  showReviewFilterDialog = false;
  tempReviewFilterComponentTypes: string[] = [];
  tempReviewFilterChangeTypes: string[] = [];
  reviewGlobalSearch = '';

  // Step 3: Merge Options
  createTagBeforeMerge = true; // Always true, not editable
  tagName: string = '';
  tagValidationError: string = '';
  isValidatingTag = false;
  isTagDuplicate = false;
  hasUserEditedTagName = false; // Track if user has manually edited the tag name
  publishAfterMerge = false;

  // Merge completed dialog
  showMergeCompletedDialog = false;
  hasPublishError = false;

  // Merge failed dialog
  showMergeFailedDialog = false;
  mergeFailedMessage = '';

  // Flag to track if merge API has been called (for showing different loading message)
  isMergeInProgress = false;

  // Track if Step 2 has been visited (for ignoreOnGoingMergeValidation flag)
  private step2Visited = false;

  // Merge ID from API response
  mergeId: string | null = null;

  unPublishedSource: boolean = false;
  unPublishedTarget: boolean = false;

  // Avatar color cache
  private readonly avatarColorCache = new Map<string, string>();

  // Scroll listener cleanup function
  private scrollListenerCleanup: (() => void) | null = null;

  // setTimeout reference for scroll listener attachment (cleared on destroy)
  private scrollAttachTimeout: any = null;

  // Component name lookup map for building paths (componentId ? names)
  private readonly componentNameLookup = new Map<
    string,
    { sourceName: string; targetName: string; componentType: string }
  >();

  // Firestore subscription for merge status monitoring
  private mergeStatusSubscription: Subscription | null = null;

  // Backup subscription to MergeStatusService.mergeStatus$ for cross-tab detection
  private mergeServiceSubscription: Subscription | null = null;

  // Date formats from global constants
  defaultDateDisplayFormat = GlobalConstants.defaultDateDisplayFormat;
  defaultDateTimeDisplayFormat = GlobalConstants.defaultDateTimeDisplayFormat;
  applicationType: 'INDEPENDENT' | 'MEMBER' | 'SUITE' | '' = '';

  @Output() dialogClosed = new EventEmitter<void>();

  // Store the defaultAppId passed from parent
  private _defaultAppId: string = '';

  ngOnInit(): void {
    this._defaultAppId = this.dialogConfig?.data?.defaultAppId || '';
    this.sourceBranch = this.dialogConfig?.data?.sourceBranch;
    this.checkForUnpublishedChanges(this.sourceBranch?.sid || '', 'source');
    this.applicationType = this.sourceBranch?.applicationType || '';
    this.loadAvailableBranches();

    // Mark that user is initiating merge to prevent popup for their own merge
    this.mergeStatusService.markAsUserInitiatingMerge();

    // Check for ongoing merge on source branch when dialog opens
    if (this.sourceBranch?.sid) {
      this.validateOngoingMergeForBranch(this.sourceBranch.sid, 'source');
    }
    // Reset application filter state
    this.isSuiteMerge = this.applicationType === 'SUITE';
    this.applicationOptions = [];
    this.filterApplications = [];
  }

  ngOnDestroy(): void {
    // Cleanup Firestore subscription
    this.unsubscribeFromMergeStatus();

    this.mergeStatusService.clearUserInitiatingMerge();
    if (!this.isMergeInProgress && !this.mergeId) {
      this.mergeStatusService.clearMergeState();
    }

    // Cleanup tag validation subscription
    this.tagValidationSubscription?.unsubscribe();
    this.tagValidationSubject.complete();

    // Cleanup scroll listener
    this.detachTableScrollListener();

    // Cleanup backup merge service subscription
    this.mergeServiceSubscription?.unsubscribe();
    this.mergeServiceSubscription = null;
  }

  /**
   * Common method to fetch branches or tags from repo service
   * @param orgId - Organization ID
   * @param refType - Type of reference: 'BRANCH' or 'TAG'
   * @param filters - Filters to apply (e.g., { refsName: 'tagName' })
   * @param parentAppSid - Parent application SID
   * @param onSuccess - Callback function with response data
   * @param onError - Optional callback function for error handling
   */
  private getRefsFromRepoService(
    orgId: string,
    refType: 'BRANCH' | 'TAG',
    filters: any,
    parentAppSid: string,
    onSuccess: (response: any) => void,
    onError?: (error: any) => void
  ): void {
    this.repoService
      .getBranchesOrTags({
        orgId,
        refType,
        filters,
        sort: '',
        page: 1,
        length: 100,
        parentAppSid
      })
      .subscribe({
        next: (response: any) => onSuccess(response),
        error: (error: any) => {
          if (onError) {
            onError(error);
          }
        }
      });
  }

  /**
   * Subscribe to Firestore to monitor merge status changes (e.g., cancelled by another user)
   */
  private subscribeToMergeStatus(): void {
    if (!this.mergeId) {
      return;
    }

    // Unsubscribe from any existing subscription
    this.unsubscribeFromMergeStatus();

    // Try to get appVersion from appGlobalService (when user is inside an app)
    // Fallback to sourceBranch.rappitVersion (when user is on lifecycle page)
    const appVersion = this.appGlobalService.get('appVersion') || this.sourceBranch?.rappitVersion;
    if (!appVersion) {
      return;
    }

    const collectionName = `MergeHistoryTracker_${appVersion}`;
    const queryObject = {
      collectionName: collectionName,
      docId: this.mergeId
    };

    // Use fsValueChanges for single document subscription (stateChanges is only for collections)
    // --- Build applicationOptions from distinct applicationName values ---
    if (this.isSuiteMerge) {
      const appNames = new Set<string>();
      for (const change of this.allMergeChanges) {
        const appName = (change as any).applicationName as string | undefined;
        if (appName) {
          appNames.add(appName);
        }
      }
      this.applicationOptions = Array.from(appNames).map((name) => ({ label: name, value: name }));
    } else {
      this.applicationOptions = [];
    }

    this.mergeStatusSubscription = this.fsService.fsValueChanges(queryObject).subscribe({
      next: (res: any) => this.handleMergeStatusChange(res),
      error: (error: any) => console.error('Error subscribing to MergeHistoryTracker:', error)
    });
    this.mergeServiceSubscription?.unsubscribe();
    this.mergeServiceSubscription = this.mergeStatusService.mergeStatus$.pipe(skip(1)).subscribe((status) => {
      // Only react when merge has ended and we still have an active mergeId
      // (if mergeId is null, the document listener already handled it)
      if (!status.isMergeInProgress && this.mergeId) {
        // Skip if user is actively cancelling or changing target branch in this tab
        if (this.isUserCancellingMerge || this.isUserChangingTargetBranch) {
          return;
        }

        if (status.status === MergeStatus.MERGE_CANCELLED) {
          const appMetaInfo = this.appGlobalService.get('appmetaInfo') || {};
          const cancelledByName =
            (status.cancelledById && appMetaInfo[status.cancelledById]) || status.cancelledBy || '';
          this.handleMergeCancelledByOtherUser(cancelledByName);
        } else if (status.status === MergeStatus.MERGE_FAILED) {
          this.handleMergeFailed('');
        } else if (
          status.status === MergeStatus.MERGE_COMPLETED ||
          status.status === 'MERGE_COMPLETED_WITH_PUBLISH_ERROR'
        ) {
          this.handleMergeCompleted(status.status === 'MERGE_COMPLETED_WITH_PUBLISH_ERROR');
        }
      }
    });
  }

  /**
   * Handle merge status changes from Firestore
   */
  private handleMergeStatusChange(docData: any): void {
    if (!docData) {
      return;
    }

    // Skip if user is changing target branch (they intentionally triggered the cancellation)
    if (this.isUserChangingTargetBranch) {
      return;
    }

    // Check if merge has completed
    // This happens after the /merge API finishes processing - close dialog and show completion popup
    if (
      docData.status === MergeStatus.MERGE_COMPLETED ||
      docData.status === MergeStatus.MERGE_COMPLETED_WITH_PUBLISH_ERROR
    ) {
      this.handleMergeCompleted(docData.status === MergeStatus.MERGE_COMPLETED_WITH_PUBLISH_ERROR);
      return;
    }

    // Check if merge has failed
    if (docData.status === MergeStatus.MERGE_FAILED) {
      this.handleMergeFailed(docData.errorMessage || '');
      return;
    }

    // Check if merge was cancelled
    if (docData.status === MergeStatus.MERGE_CANCELLED) {
      // Skip if current user is actively cancelling the merge in THIS tab
      // (the cancel handler in this tab will close the dialog itself)
      if (this.isUserCancellingMerge) {
        return;
      }

      const cancelledBy = docData.modifiedBy || docData.createdBy;
      const appMetaInfo = this.appGlobalService.get('appmetaInfo') || {};
      const cancelledByName = appMetaInfo[cancelledBy] || cancelledBy;

      // Close dialog - whether cancelled by same user from another tab or by a different user
      this.handleMergeCancelledByOtherUser(cancelledByName);
    }
  }

  /**
   * Handle when merge has completed successfully (detected via Firestore or API response)
   * Shows the Merge Completed p-dialog with "Go to target application" link.
   * The dialog stays open until the user clicks Close.
   * @param hasPublishError - true if merge completed but publish to git failed
   */
  private handleMergeCompleted(hasPublishError = false): void {
    // Prevent duplicate calls from multiple listeners (Firestore, API response, mergeStatus$)
    if (this.showMergeCompletedDialog || !this.isMergeInProgress) {
      return;
    }

    if (this.mergeId) {
      this.mergeStatusService.markMergeAsHandled(this.mergeId);
    }

    // Unsubscribe to prevent further updates
    this.unsubscribeFromMergeStatus();

    // Clear mergeId so ngOnDestroy won't send a cancel request
    this.mergeId = null;

    // Stop the spinner
    this.isMergeInProgress = false;
    this.loadingReviewChanges = false;

    // Show the merge completed p-dialog (in-dialog popup with "Go to target" link)
    this.hasPublishError = hasPublishError;
    this.showMergeCompletedDialog = true;
  }

  /**
   * Handle when merge has failed (detected via Firestore or API response)
   * Shows the Merge Failed p-dialog with the error message.
   */
  private handleMergeFailed(errorMessage: string): void {
    // Prevent duplicate calls or calls when merge hasn't actually been started from this dialog
    if (!this.isMergeInProgress) {
      return;
    }

    // Mark this merge as handled so the service won't show a popup for it
    // after the dialog closes and clears isUserInitiatingMerge
    if (this.mergeId) {
      this.mergeStatusService.markMergeAsHandled(this.mergeId);
    }

    // Unsubscribe to prevent further updates
    this.unsubscribeFromMergeStatus();

    // Clear mergeId so ngOnDestroy won't send a cancel request
    this.mergeId = null;

    // Stop the spinner
    this.isMergeInProgress = false;
    this.loadingReviewChanges = false;
    this.mergeStatusService.clearMergeState(MergeStatus.MERGE_FAILED);

    // Show the merge failed p-dialog
    this.mergeFailedMessage = errorMessage || this.translate.instant('MERGE_FAILED_POPUP_MESSAGE');
    this.showMergeFailedDialog = true;
  }

  /**
   * Close the merge failed dialog
   */
  closeMergeFailedDialog(): void {
    this.showMergeFailedDialog = false;
    this.mergeFailedMessage = '';
    // Ensure merge state is fully cleared before closing
    this.mergeStatusService.clearMergeState(MergeStatus.MERGE_FAILED);
    this.dialogRef.close();
  }

  /**
   * Get the URL for the target application
   */
  getTargetApplicationUrl(): string {
    if (!this.targetBranch) {
      return '';
    }
    const rappitVersion = this.targetBranch.rappitVersion || '';
    const orgId = this.targetBranch.orgId || this.appGlobalService.get('orgId');
    const appSid = this.targetBranch.sid;
    if (rappitVersion === environment.rappitVersion) {
      return `${globalThis.location.origin}/#/home/${orgId}/${appSid}/gathering/documents`;
    }
    return `${globalThis.location.origin}/${rappitVersion}/#/home/${orgId}/${appSid}/gathering/documents`;
  }

  /**
   * Close the merge completed dialog and the merge branch dialog
   */
  closeMergeCompletedDialog(): void {
    this.showMergeCompletedDialog = false;
    this.hasPublishError = false;
    this.dialogRef.close({
      saved: true,
      merged: true,
      sourceBranch: this.sourceBranch,
      targetBranch: this.targetBranch
    });
  }

  /**
   * Handle when merge is cancelled by another user
   */
  private handleMergeCancelledByOtherUser(cancelledByName: string): void {
    // Mark this merge as handled so the service won't show a duplicate popup
    // after the dialog closes and clears isUserInitiatingMerge
    if (this.mergeId) {
      this.mergeStatusService.markMergeAsHandled(this.mergeId);
    }

    // Unsubscribe to prevent further updates
    this.unsubscribeFromMergeStatus();

    // Clear mergeId to prevent sending cancel request on dialog close
    this.mergeId = null;

    // Show message using global MessageService (toast in app.component)
    this.messageService.add({
      severity: 'warn',
      summary: this.translate.instant('MERGE_CANCELLED'),
      detail: this.translate.instant('MERGE_CANCELLED_BY_USER', { userName: cancelledByName }),
      sticky: true
    });

    // Close dialog immediately - toast will persist since it's in app.component
    this.dialogRef.close();
    this.dialogClosed.emit();
  }

  /**
   * Unsubscribe from merge status Firestore listener and backup service subscription
   */
  private unsubscribeFromMergeStatus(): void {
    if (this.mergeStatusSubscription) {
      this.mergeStatusSubscription.unsubscribe();
      this.mergeStatusSubscription = null;
    }
    if (this.mergeServiceSubscription) {
      this.mergeServiceSubscription.unsubscribe();
      this.mergeServiceSubscription = null;
    }
  }

  private get defaultAppId(): string {
    return this._defaultAppId;
  }

  private loadAvailableBranches(): void {
    if (!this.defaultAppId) {
      return;
    }

    this.loadingBranches = true;
    const orgId = this.appGlobalService.get('orgId') as string;

    this.getRefsFromRepoService(
      orgId,
      'BRANCH',
      {},
      this.defaultAppId,
      (response) => {
        const allBranches = response.data.map((node) => node.data as BranchData);

        // Build appSid -> appName lookup for suite merges
        if (this.isSuiteMerge) {
          this.appSidToNameMap.clear();
          for (const branch of allBranches) {
            const appSid = branch.parentAppSid || branch.defaultAppSid || '';
            const appName = branch.name || '';
            if (appSid && appName && !this.appSidToNameMap.has(appSid)) {
              this.appSidToNameMap.set(appSid, appName);
            }
          }
        }

        // Cache source branch data from API (has date fields from Elasticsearch for comparison date)
        const sourceBranchFull = allBranches.find((branch) => branch.sid === this.sourceBranch?.sid);
        if (sourceBranchFull) {
          this.sourceBranchFromApi = sourceBranchFull;
        }

        // Filter out source branch name and deduplicate (suite mode returns one per member app)
        const seen = new Set<string>();
        const branches = allBranches.filter((branch) => {
          if (branch.refsName === this.sourceBranch?.refsName) return false;
          if (seen.has(branch.refsName)) return false;
          seen.add(branch.refsName);
          return true;
        });

        this.availableBranches = branches.map((branch) => ({
          label: branch.refsName,
          value: branch
        }));

        // Set default target branch to parent branch of source branch
        // Use the option's value to ensure reference equality with p-select
        if (!this.targetBranch && this.sourceBranch?.sourceBranchAppSid) {
          const parentBranchOption = this.availableBranches.find(
            (option) => option.value.sid === this.sourceBranch?.sourceBranchAppSid
          );
          if (parentBranchOption) {
            this.targetBranch = parentBranchOption.value;
            this.checkForUnpublishedChanges(this.targetBranch?.sid || '', 'target');
            // Validate ongoing merge for auto-selected target branch
            if (this.targetBranch.sid) {
              this.validateOngoingMergeForBranch(this.targetBranch.sid, 'target');
            }
          }
        }

        this.initialTargetBranchId = this.targetBranch?.sid || null;

        this.loadingBranches = false;
      },
      () => {
        this.loadingBranches = false;
      }
    );
  }

  get isStep1Complete(): boolean {
    return !!this.sourceBranch && !!this.targetBranch && !this.targetBranchHasOngoingMerge && !this.targetBranchDeleted;
  }

  /**
   * Re-validate that the selected target branch still exists by re-fetching branches.
   * Calls onValid callback if branch exists, otherwise sets targetBranchDeleted flag.
   */
  private revalidateTargetBranch(onValid: () => void): void {
    if (!this.targetBranch || !this.defaultAppId) {
      return;
    }

    const orgId = this.appGlobalService.get('orgId') as string;
    const targetSid = this.targetBranch.sid;

    this.loadingBranches = true;

    this.getRefsFromRepoService(
      orgId,
      'BRANCH',
      {},
      this.defaultAppId,
      (response) => {
        const allBranches = response.data.map((node) => node.data as BranchData);

        // Cache source branch data from API (has date fields for comparison date in Step 2)
        const sourceBranchFull = allBranches.find((branch) => branch.sid === this.sourceBranch?.sid);
        if (sourceBranchFull) {
          this.sourceBranchFromApi = sourceBranchFull;
        }

        const seen2 = new Set<string>();
        const branches = allBranches.filter((branch) => {
          if (branch.refsName === this.sourceBranch?.refsName) return false;
          if (seen2.has(branch.refsName)) return false;
          seen2.add(branch.refsName);
          return true;
        });

        this.availableBranches = branches.map((branch) => ({
          label: branch.refsName,
          value: branch
        }));

        const branchStillExists = branches.some((branch) => branch.sid === targetSid);

        if (branchStillExists) {
          this.targetBranchDeleted = false;
          this.loadingBranches = false;
          onValid();
        } else {
          // Target branch was deleted externally
          this.targetBranch = null;
          this.targetBranchDeleted = true;
          this.loadingBranches = false;
        }
      },
      () => {
        this.loadingBranches = false;
        // On error, proceed optimistically
        onValid();
      }
    );
  }

  /**
   * Handle target branch change - cancel existing merge if one exists
   */
  onTargetBranchChange(event: any): void {
    this.checkForUnpublishedChanges(this.targetBranch?.sid || '', 'target');
    // Clear deleted flag when user selects a new branch
    this.targetBranchDeleted = false;
    // If merge history exists (mergeId), cancel it and reset state
    if (this.mergeId) {
      // Set flag to skip Firestore handler - we're intentionally changing the target branch
      this.isUserChangingTargetBranch = true;

      // Unsubscribe from Firestore to prevent race conditions
      this.unsubscribeFromMergeStatus();

      // Mark as cancelled by user to prevent showing merge completed popup
      this.mergeStatusService.markAsCancelledByUser();

      this.repoService.cancelMergeHistoryTracker(this.mergeId).subscribe({
        next: () => {
          this.resetMergeState();
          this.isUserChangingTargetBranch = false;
          // Check for ongoing merge on the newly selected target branch
          if (this.targetBranch?.sid) {
            this.validateOngoingMergeForBranch(this.targetBranch.sid, 'target');
          }
        },
        error: (error) => {
          console.error('Error cancelling merge on branch change:', error);
          // Reset state even if cancel fails
          this.resetMergeState();
          this.isUserChangingTargetBranch = false;
          // Check for ongoing merge on the newly selected target branch
          if (this.targetBranch?.sid) {
            this.validateOngoingMergeForBranch(this.targetBranch.sid, 'target');
          }
        }
      });
    } else if (this.targetBranch?.sid) {
      // No existing merge, just validate the newly selected target branch
      this.validateOngoingMergeForBranch(this.targetBranch.sid, 'target');
    }
  }

  /**
   * Reset merge state when target branch changes or merge is cancelled
   */
  private resetMergeState(): void {
    this.mergeId = null;
    this.mergeHistoryTrackerId = null;
    this.step2Visited = false;
    this.allMergeChanges = [];
    this.filteredMergeChanges = [];
    this.mergeChanges = [];
    this.reviewChanges = [];
    this.comparisonDate = null;
    this.noMergesDone = false;
    this.isProcessRunning = false;
    this.processWarningMessage = '';
    // Clear API call cache
    this.lastGetChangesPayload = null;
    this.lastAutoSelectPayload = null;
  }

  /**
   * Validate if there's an ongoing merge for a given branch.
   * For source branch: Close the dialog and show toast message.
   * For target branch: Show inline message and block further steps (don't close dialog).
   * @param branchId - The branch ID to check
   * @param branchType - 'source' or 'target' to indicate which branch is being checked
   */
  private validateOngoingMergeForBranch(branchId: string, branchType: 'source' | 'target'): void {
    if (!branchId) {
      return;
    }

    // Reset target branch ongoing merge state when checking
    if (branchType === 'target') {
      this.targetBranchHasOngoingMerge = false;
      this.targetBranchOngoingMergeMessage = '';
    }

    // Use checkMergeEligibility API to check if branch is eligible for merge
    this.repoService.checkMergeEligibility(branchId).subscribe({
      next: (response) => {
        // Branch is eligible for merge (no active merge in progress)
        // Clear any previous ongoing merge state for target branch
        if (branchType === 'target') {
          this.targetBranchHasOngoingMerge = false;
          this.targetBranchOngoingMergeMessage = '';
        }
      },
      error: (error) => {
        // Check if error indicates an ongoing merge (eligible: false)
        const errorBody = error?.error;
        const reason = errorBody?.reason || '';

        if (reason || errorBody?.eligible === false) {
          if (branchType === 'source') {
            // For source branch: show toast and close dialog
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('MERGE_IN_PROGRESS'),
              detail: this.translate.instant('MERGE_IN_PROGRESS_FOR_SOURCE_BRANCH'),
              life: 5000
            });

            // Close the dialog
            this.dialogRef.close();
            this.dialogClosed.emit();
          } else {
            // For target branch: show inline error and block further steps
            this.targetBranchHasOngoingMerge = true;
            this.targetBranchOngoingMergeMessage = this.translate.instant('MERGE_IN_PROGRESS_FOR_TARGET_BRANCH');
          }
        }
      }
    });
  }

  get canProceedToNextStep(): boolean {
    if (this.activeIndex === 0) {
      return this.isStep1Complete;
    }
    if (this.activeIndex === 1) {
      // Can't proceed if there are conflicts with "Pick Source" action
      // User can proceed if conflicted changes have "Pick Target" or "Don't Merge Now" action
      // At least one change must have "Pick Source" action to proceed (Pick Target alone is not enough)
      const hasPickSourceChanges = this.allMergeChanges.some((c) => c.action === 'MERGE_SOURCE');
      return !this.isProcessRunning && this.getBlockingConflictCount() === 0 && hasPickSourceChanges;
    }
    if (this.activeIndex === 2) {
      // Can't proceed to confirmation step if no changes are there to merge
      // or if tag is invalid or still validating
      return !this.hasNoChanges && !this.hasReviewConflicts() && this.isTagValid && !this.isValidatingTag && !this.loadingReviewChanges;
    }
    return true;
  }

  onClose(): void {
    // Prevent closing or cancelling when merge is in progress
    if (this.isMergeInProgress) {
      return;
    }

    const currentTargetBranchId = this.targetBranch?.sid || null;
    const hasTargetBranchChanged = currentTargetBranchId !== this.initialTargetBranchId;

    // Show cancel merge confirmation if merge history was created (mergeId exists)
    if (this.mergeId) {
      this.confirmationDialogService.confirm({
        message: this.translate.instant('CANCEL_MERGE_CONFIRMATION_MESSAGE'),
        header: this.translate.instant('CANCEL_MERGE'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('YES'),
        rejectLabel: this.translate.instant('NO'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => {
          this.cancelMergeAndClose();
        },
        reject: () => {
          // User chose to continue, do nothing
        }
      });
    } else if (hasTargetBranchChanged) {
      this.confirmationDialogService.confirm({
        message: this.translate.instant('UN_SAVED_CUD'),
        header: this.translate.instant('CONFIRM'),
        acceptLabel: this.translate.instant('YES'),
        rejectLabel: this.translate.instant('NO'),
        accept: () => {
          this.dialogRef.close();
          this.dialogClosed.emit();
        },
        reject: () => {
          // User chose to stay, do nothing
        }
      });
    } else {
      this.dialogRef.close();
      this.dialogClosed.emit();
    }
  }

  /**
   * Cancel the merge operation and close the dialog
   */
  private cancelMergeAndClose(): void {
    if (!this.mergeId) {
      this.dialogRef.close();
      this.dialogClosed.emit();
      return;
    }

    // Unsubscribe from Firestore FIRST to prevent receiving the cancel status update
    this.unsubscribeFromMergeStatus();

    // Set flag to skip any potential race condition with Firestore handler
    this.isUserCancellingMerge = true;

    // Mark as cancelled by user to prevent showing merge completed popup
    this.mergeStatusService.markAsCancelledByUser();

    // Mark this merge as handled so the service won't show a popup for it
    // after the dialog closes and the Firestore listener picks up the cancel status
    this.mergeStatusService.markMergeAsHandled(this.mergeId);

    // Clear isMergeInProgress immediately so the app becomes editable again
    // (Firestore listener is already unsubscribed, so it won't receive the cancel status to clear it)
    this.appGlobalService.write('isMergeInProgress', false);
    this.appGlobalService.write('mergeDetails', null);

    this.repoService.cancelMergeHistoryTracker(this.mergeId).subscribe({
      next: () => {
        // Show success toast message
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('MERGE_CANCELLED'),
          detail: this.translate.instant('MERGE_CANCELLED_SUCCESS'),
          life: 5000
        });
        // Close dialog with cancelled flag so parent can reload the route
        this.dialogRef.close({ cancelled: true });
        this.dialogClosed.emit();
      },
      error: (error) => {
        console.error('Error cancelling merge:', error);
        // Close dialog even if cancel fails - still pass cancelled flag
        this.dialogRef.close({ cancelled: true });
        this.dialogClosed.emit();
      }
    });
  }

  /**
   * Handle browser/tab close - send cancel request if merge is in progress
   * Using both 'beforeunload' and 'pagehide' for better browser coverage
   */
  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.sendCancelRequest();
  }

  @HostListener('window:pagehide')
  onPageHide(): void {
    this.sendCancelRequest();
  }

  private cancelRequestSent = false;

  private sendCancelRequest(): void {
    // Prevent duplicate calls (both events may fire)
    if (this.mergeId && !this.cancelRequestSent) {
      this.cancelRequestSent = true;
      // Build absolute URL
      const baseUrl = globalThis.location.origin;
      const url = `${baseUrl}/rest/v1/mergehistorytrackers/${this.mergeId}/cancel`;
      // Use fetch with keepalive to allow PUT method (sendBeacon only supports POST)
      fetch(url, {
        method: 'PUT',
        keepalive: true
      }).catch(() => {
        // Intentionally empty - fire and forget on page unload
      });
    }
  }

  prevStep(): void {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    }
  }

  nextStep(): void {
    if (this.activeIndex < 3 && this.canProceedToNextStep) {
      if (this.activeIndex === 0) {
        // Re-validate target branch still exists before proceeding
        this.revalidateTargetBranch(() => {
          // Moving from Step 1 to Step 2, load changes only if payload changed
          const currentPayload = {
            sourceBranchId: this.sourceBranch?.sid || '',
            targetBranchId: this.targetBranch?.sid || ''
          };
          const isSamePayload =
            this.lastGetChangesPayload &&
            this.lastGetChangesPayload.sourceBranchId === currentPayload.sourceBranchId &&
            this.lastGetChangesPayload.targetBranchId === currentPayload.targetBranchId;

          if (!isSamePayload) {
            this.loadMergeChanges();
          }
          this.activeIndex++;
        });
        return;
      }
      if (this.activeIndex === 1) {
        // Moving from Step 2 to Step 3, load auto-select changes only if payload changed
        const shouldLoadAutoSelect = this.shouldLoadAutoSelectChanges();
        if (shouldLoadAutoSelect) {
          this.loadAutoSelectChanges();
        }
        // Generate default tag name only if user hasn't manually edited it
        if (!this.hasUserEditedTagName) {
          this.generateDefaultTagName();
        }
      }
      this.activeIndex++;
    }
  }

  /**
   * Check if autoSelect API should be called based on payload comparison
   */
  private shouldLoadAutoSelectChanges(): boolean {
    const pickSourceComponents = this.allMergeChanges.filter((c) => c.action === 'MERGE_SOURCE');
    const selectedComponents = this.buildSelectedComponentsMap(pickSourceComponents);

    for (const key of Object.keys(selectedComponents)) {
      selectedComponents[key].sort((a: string, b: string) => a.localeCompare(b));
    }

    const currentPayloadStr = JSON.stringify(
      selectedComponents,
      Object.keys(selectedComponents).sort((a, b) => a.localeCompare(b))
    );

    return this.lastAutoSelectPayload !== currentPayloadStr;
  }

  private buildSelectedComponentsMap(components: any[]): Record<string, string[]> {
    const map: Record<string, string[]> = {};

    for (const c of components) {
      if (c.groupedComponentIds?.length && c.groupedComponentTypes) {
        this.addGroupedComponentIds(c, map);
      } else if (!c.isSyntheticWorkflow && !c.isSyntheticQueryView && !c.isSyntheticEnvironment && !c.isSyntheticTechChoices) {
        this.addUniqueId(map, c.componentType, c.componentId);
      }
    }

    return map;
  }

  private addGroupedComponentIds(component: any, map: Record<string, string[]>): void {
    for (const id of component.groupedComponentIds) {
      const type = component.groupedComponentTypes[id] || component.componentType;
      this.addUniqueId(map, type, id);
    }
  }

  private addUniqueId(map: Record<string, string[]>, type: string, id: string): void {
    if (!map[type]) {
      map[type] = [];
    }
    if (!map[type].includes(id)) {
      map[type].push(id);
    }
  }

  /**
   * Generate default tag name with timestamp
   */
  private generateDefaultTagName(): void {
    const now = new Date();
    // Format: YYYYMMDD_HHmmss
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;
    this.tagName = `${this.sourceBranch?.refsName || ''}_${this.targetBranch?.refsName || ''}_${timestamp}`;
  }

  // Subject for debouncing tag duplicate check
  private readonly tagValidationSubject = new Subject<string>();
  private tagValidationSubscription: Subscription | null = null;

  /**
   * Initialize tag validation debounce subscription
   */
  private initTagValidationDebounce(): void {
    this.tagValidationSubscription = this.tagValidationSubject.pipe(debounceTime(500)).subscribe((tagName) => {
      this.checkTagDuplicate(tagName);
    });
  }

  /**
   * Validate tag name format and check for duplicates
   * Uses the same validation rules as create-tag-popup (via UtilService.gitNameValidator)
   */
  validateTagName(): void {
    // Mark that user has edited the tag name
    this.hasUserEditedTagName = true;

    this.tagValidationError = '';
    this.isTagDuplicate = false;

    // Check if empty
    if (!this.tagName?.trim()) {
      this.tagValidationError = this.translate.instant('TAG_NAME_REQUIRED');
      return;
    }

    // Use the same validator as create-tag-popup
    const validator = this.utilService.gitNameValidator();
    const validationResult = validator({ value: this.tagName } as any);

    if (validationResult?.['gitNameError']) {
      this.tagValidationError = validationResult['gitNameError'];
      return;
    }

    // Initialize debounce subscription if not already done
    if (!this.tagValidationSubscription) {
      this.initTagValidationDebounce();
    }

    // Trigger debounced duplicate check
    this.isValidatingTag = true;
    this.tagValidationSubject.next(this.tagName.trim());
  }

  /**
   * Check if tag name already exists
   */
  private checkTagDuplicate(tagName: string): void {
    if (!tagName) {
      this.isValidatingTag = false;
      return;
    }

    const orgId = this.appGlobalService.get('orgId') as string;
    const parentAppSid = this.sourceBranch?.parentAppSid || '';

    this.getRefsFromRepoService(
      orgId,
      'TAG',
      { refsName: tagName },
      parentAppSid,
      (response) => {
        this.isValidatingTag = false;
        // Only update if the tagName hasn't changed since the request was made
        if (tagName === this.tagName.trim() && response.total > 0) {
          this.isTagDuplicate = true;
          this.tagValidationError = this.translate.instant('TAG_NAME_ALREADY_EXISTS');
        }
      },
      () => {
        this.isValidatingTag = false;
      }
    );
  }

  /**
   * Check if tag is valid for merge
   */
  get isTagValid(): boolean {
    return !!this.tagName?.trim() && !this.tagValidationError && !this.isTagDuplicate && !this.isValidatingTag;
  }

  /**
   * Step 3: Load auto-selected components from API
   */
  private loadAutoSelectChanges(): void {
    if (!this.sourceBranch?.sid || !this.targetBranch?.sid) {
      return;
    }

    const pickSourceComponents = this.allMergeChanges.filter((c) => c.action === 'MERGE_SOURCE');
    this.userSelectedComponentIds = this.buildUserSelectedComponentIds(pickSourceComponents);

    this.loadingReviewChanges = true;
    this.reviewError = '';
    this.reviewChanges = [];
    this.filteredReviewChanges = [];

    if (this.isSuiteMerge) {
      this.loadAutoChangesSuite(pickSourceComponents);
    } else {
      const selectedComponents = this.buildSelectedComponentsMap(pickSourceComponents);
      const requestArray: AutoSelectRequest[] = [
        {
          selectedComponents,
          sourceBranchId: this.sourceBranch.sid,
          targetBranchId: this.targetBranch.sid,
          ignoreOnGoingMergeValidation: true,
          mergeId: this.mergeId || undefined
        }
      ];
      this.repoService.getMergeAutoSelect(requestArray).subscribe({
        next: (response: any) => this.handleAutoSelectResponse(response, selectedComponents),
        error: (error) => this.handleAutoSelectError(error)
      });
    }
  }

  /**
   * Suite mode: send single API call with array payload grouped per source branch
   */
  private loadAutoChangesSuite(pickSourceComponents: MergeChange[]): void {
    const branchGroups = this.groupBySourceAppId(pickSourceComponents);

    // Build array payload - one entry per branch
    const requestArray: AutoSelectRequest[] = [];
    for (const [sourceAppId, components] of branchGroups) {
      const selectedComponents = this.buildSelectedComponentsMap(components);
      const targetAppId = components[0]?.targetAppId || this.targetBranch!.sid!;
      requestArray.push({
        selectedComponents,
        sourceBranchId: sourceAppId,
        targetBranchId: targetAppId,
        ignoreOnGoingMergeValidation: true,
        mergeId: this.mergeId || undefined
      });
    }

    if (requestArray.length === 0) {
      this.loadingReviewChanges = false;
      return;
    }

    const allSelectedComponents = this.buildSelectedComponentsMap(pickSourceComponents);

    // Build componentId -> {sourceAppId, targetAppId} lookup from request context
    const appIdFromRequest = new Map<string, { sourceAppId: string; targetAppId: string }>();
    for (const entry of requestArray) {
      for (const ids of Object.values(entry.selectedComponents)) {
        for (const id of ids) {
          appIdFromRequest.set(id, { sourceAppId: entry.sourceBranchId, targetAppId: entry.targetBranchId });
        }
      }
    }

    // Single API call with array payload
    this.repoService.getMergeAutoSelect(requestArray).subscribe({
      next: (response: any) => {
        // Inject sourceAppId/targetAppId from request context into each component detail
        // Response array items correspond 1:1 with requestArray entries
        this.injectAppIdsIntoResponse(response, requestArray);

        this.handleAutoSelectResponse(response, allSelectedComponents);

        // Post-process: fill in sourceAppId/targetAppId on reviewChanges from allMergeChanges and request context
        this.populateReviewChangesAppIds(appIdFromRequest);
      },
      error: (error) => this.handleAutoSelectError(error)
    });
  }

  /** Checks whether a component needs per-app splitting (synthetic env/techChoices with app ID map). */
  private needsPerAppSplit(component: MergeChange): boolean {
    return !!(component.isSyntheticEnvironment || component.isSyntheticTechChoices) && !!component.groupedComponentAppIds;
  }

  /** Splits a synthetic env/techChoices group into one virtual MergeChange per sourceAppId. */
  private buildPerAppVirtualComponents(component: MergeChange): MergeChange[] {
    const appIdMap = component.groupedComponentAppIds!;
    const perApp = new Map<string, MergeChange>();

    for (const id of component.groupedComponentIds || []) {
      const appIds = appIdMap[id];
      const sourceKey = appIds?.sourceAppId || component.sourceAppId || this.sourceBranch!.sid!;

      if (!perApp.has(sourceKey)) {
        perApp.set(sourceKey, {
          ...component,
          sourceAppId: sourceKey,
          targetAppId: appIds?.targetAppId || component.targetAppId || this.targetBranch!.sid!,
          groupedComponentIds: [],
          groupedComponentTypes: {}
        });
      }
      const virtual = perApp.get(sourceKey)!;
      virtual.groupedComponentIds!.push(id);
      virtual.groupedComponentTypes![id] = component.groupedComponentTypes?.[id] || component.componentType;
    }

    return Array.from(perApp.values());
  }

  /** Resolves per-app splits and groups changes by sourceAppId. */
  private groupBySourceAppId(changes: MergeChange[]): Map<string, MergeChange[]> {
    const groups = new Map<string, MergeChange[]>();
    for (const change of changes) {
      const resolved = this.needsPerAppSplit(change)
        ? this.buildPerAppVirtualComponents(change)
        : [change];
      for (const c of resolved) {
        const key = c.sourceAppId || this.sourceBranch!.sid!;
        if (!groups.has(key)) {
          groups.set(key, []);
        }
        groups.get(key)!.push(c);
      }
    }
    return groups;
  }

  /**
   * Injects sourceAppId/targetAppId from the request context into each component detail of the response.
   * Response array items correspond 1:1 with requestArray entries.
   */
  private injectAppIdsIntoResponse(response: any, requestArray: AutoSelectRequest[]): void {
    if (!Array.isArray(response)) {
      return;
    }
    const count = Math.min(response.length, requestArray.length);
    for (let i = 0; i < count; i++) {
      const details = response[i]?.componentChangeDetails || {};
      this.applyAppIdsToDetails(details, requestArray[i].sourceBranchId, requestArray[i].targetBranchId);
    }
  }

  /** Applies the given source/target app ids to every component detail entry within the details map. */
  private applyAppIdsToDetails(details: Record<string, any>, sourceAppId: string, targetAppId: string): void {
    for (const componentTypeMap of Object.values(details)) {
      if (!componentTypeMap || typeof componentTypeMap !== 'object') {
        continue;
      }
      for (const detail of Object.values(componentTypeMap as Record<string, any>)) {
        if (detail && typeof detail === 'object') {
          detail.sourceAppId = sourceAppId;
          detail.targetAppId = targetAppId;
        }
      }
    }
  }

  private buildUserSelectedComponentIds(components: any[]): Set<string> {
    const ids = new Set<string>();
    for (const c of components) {
      if (c.groupedComponentIds?.length && c.groupedComponentTypes) {
        for (const id of c.groupedComponentIds) {
          const type = c.groupedComponentTypes![id] || c.componentType;
          ids.add(`${type}_${id}`);
        }
      } else {
        ids.add(`${c.componentType}_${c.componentId}`);
      }
    }
    return ids;
  }

  private handleAutoSelectResponse(response: any, selectedComponents: Record<string, string[]>): void {
    // Response can be array (suite merge) or single object
    let nestedComponents: Record<string, Record<string, any>>;
    if (Array.isArray(response)) {
      nestedComponents = {};
      for (const item of response) {
        const details = item.componentChangeDetails || {};
        for (const [componentType, components] of Object.entries(details)) {
          if (!nestedComponents[componentType]) {
            nestedComponents[componentType] = {};
          }
          Object.assign(nestedComponents[componentType], components as any);
        }
      }
    } else {
      nestedComponents = response.componentChangeDetails || response || {};
    }
    this.buildComponentNameLookup(nestedComponents as any);

    const flatComponents = this.flattenComponentChangeDetails(nestedComponents as any);
    this.reviewChanges = this.transformAutoSelectResponse(flatComponents, this.userSelectedComponentIds);
    this.filteredReviewChanges = [...this.reviewChanges];
    this.initReviewComponentTypeOptions();
    this.buildGroupedReviewChanges();

    for (const key of Object.keys(selectedComponents)) {
      selectedComponents[key].sort((a: string, b: string) => a.localeCompare(b));
    }
    this.lastAutoSelectPayload = JSON.stringify(
      selectedComponents,
      Object.keys(selectedComponents).sort((a, b) => a.localeCompare(b))
    );

    this.loadingReviewChanges = false;
  }

  private handleAutoSelectError(error: any): void {
    console.error('Error loading auto-select changes:', error);
    const errorBody = error?.error;
    this.reviewError =
      errorBody?.MESSAGE || errorBody?.message || this.translate.instant('FAILED_TO_LOAD_REVIEW_CHANGES');
    this.loadingReviewChanges = false;
  }

  /**
   * Post-process reviewChanges to populate sourceAppId/targetAppId for suite merges.
   * Uses allMergeChanges (from Step 2 comparison API which has sourceAppId per component)
   * and the request context map as sources of truth.
   */
  private populateReviewChangesAppIds(
    appIdFromRequest: Map<string, { sourceAppId: string; targetAppId: string }>
  ): void {
    // Build componentId -> {sourceAppId, targetAppId} lookup from allMergeChanges
    const appIdLookup = this.buildAppIdLookupFromMergeChanges();

    // Merge request context into lookup (covers user-selected components)
    for (const [id, appIds] of appIdFromRequest) {
      if (!appIdLookup.has(id)) {
        appIdLookup.set(id, appIds);
      }
    }

    // Apply to reviewChanges
    for (const change of this.reviewChanges) {
      this.applyAppIdsToReviewChange(change, appIdLookup);
    }
  }

  /** Builds a componentId -> {sourceAppId, targetAppId} lookup from allMergeChanges. */
  private buildAppIdLookupFromMergeChanges(): Map<string, { sourceAppId: string; targetAppId: string }> {
    const appIdLookup = new Map<string, { sourceAppId: string; targetAppId: string }>();
    for (const change of this.allMergeChanges) {
      if (!change.sourceAppId) {
        continue;
      }
      const appIds = { sourceAppId: change.sourceAppId, targetAppId: change.targetAppId || '' };
      appIdLookup.set(change.componentId, appIds);
      for (const id of change.groupedComponentIds || []) {
        // Use per-component app IDs from groupedComponentAppIds if available (env/techChoices groups)
        const perComponentAppIds = change.groupedComponentAppIds?.[id];
        appIdLookup.set(id, perComponentAppIds || appIds);
      }
    }
    return appIdLookup;
  }

  /** Populates sourceAppId/targetAppId on a single review change using the lookup (direct or grouped). */
  private applyAppIdsToReviewChange(
    change: any,
    appIdLookup: Map<string, { sourceAppId: string; targetAppId: string }>
  ): void {
    if (change.sourceAppId) {
      return;
    }
    const lookup = appIdLookup.get(change.componentId) || this.findGroupedAppIds(change, appIdLookup);
    if (lookup) {
      change.sourceAppId = lookup.sourceAppId;
      change.targetAppId = lookup.targetAppId;
    }
  }

  /** Finds app ids for a grouped review change by checking any of its member component ids. */
  private findGroupedAppIds(
    change: any,
    appIdLookup: Map<string, { sourceAppId: string; targetAppId: string }>
  ): { sourceAppId: string; targetAppId: string } | undefined {
    for (const id of change.groupedComponentIds || []) {
      const groupLookup = appIdLookup.get(id);
      if (groupLookup) {
        return groupLookup;
      }
    }
    return undefined;
  }

  /**
   * Transform API response to ReviewMergeChange array with auto-selected flag
   */
  private transformAutoSelectResponse(
    components: Record<string, ComponentDetail>,
    userSelectedIds: Set<string>
  ): ReviewMergeChange[] {
    const entries = Object.entries(components);
    const allComponentsMap = this.buildAllComponentsMap(entries);
    const grouping = this.categorizeComponentGroups(entries);

    const result: ReviewMergeChange[] = [];
    const workflowParentIds = new Set<string>();
    const queryViewParentIds = new Set<string>();
    const environmentParentIds = new Set<string>();
    const techChoicesParentIds = new Set<string>();

    this.processReviewGroupEntries(
      grouping.workflowGroups,
      allComponentsMap,
      workflowParentIds,
      'workflow',
      userSelectedIds,
      result
    );
    this.processReviewGroupEntries(
      grouping.queryViewGroups,
      allComponentsMap,
      queryViewParentIds,
      'queryView',
      userSelectedIds,
      result
    );
    this.processReviewGroupEntries(
      grouping.environmentGroups,
      allComponentsMap,
      environmentParentIds,
      'environment',
      userSelectedIds,
      result
    );
    this.processReviewGroupEntries(
      grouping.techChoicesGroups,
      allComponentsMap,
      techChoicesParentIds,
      'techChoices',
      userSelectedIds,
      result
    );
    this.processIndividualReviewComponents(
      entries,
      components,
      allComponentsMap,
      {
        componentsWithWorkflowId: grouping.componentsWithWorkflowId,
        workflowParentIds,
        componentsWithTableId: grouping.componentsWithTableId,
        queryViewParentIds,
        componentsInEnvironmentGroup: grouping.componentsInEnvironmentGroup,
        componentsInTechChoicesGroup: grouping.componentsInTechChoicesGroup
      },
      userSelectedIds,
      result
    );
    this.populateDependentComponentNames(result);

    return result;
  }

  private buildAllComponentsMap(
    entries: [string, ComponentDetail][]
  ): Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }> {
    const map = new Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }>();
    for (const [compositeKey, detail] of entries) {
      map.set(detail.componentId, { compositeKey, componentId: detail.componentId, detail });
    }
    return map;
  }

  private categorizeComponentGroups(entries: [string, ComponentDetail][]): {
    workflowGroups: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }[]>;
    queryViewGroups: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }[]>;
    environmentGroups: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }[]>;
    techChoicesGroups: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }[]>;
    componentsWithWorkflowId: Set<string>;
    componentsWithTableId: Set<string>;
    componentsInEnvironmentGroup: Set<string>;
    componentsInTechChoicesGroup: Set<string>;
  } {
    const workflowGroups = new Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }[]>();
    const componentsWithWorkflowId = new Set<string>();
    const queryViewGroups = new Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }[]>();
    const componentsWithTableId = new Set<string>();
    const environmentGroups = new Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }[]>();
    const componentsInEnvironmentGroup = new Set<string>();
    const techChoicesGroups = new Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }[]>();
    const componentsInTechChoicesGroup = new Set<string>();
    const queryViewChildTypes = new Set<string>(['QUERY_VIEW_FIELD', 'QUERY_VIEW_CUD', 'QUERYVIEW_CUD_CONFIGURATION']);

    for (const [compositeKey, detail] of entries) {
      const workflowId = detail.additionalInfo?.workflowId;
      if (workflowId) {
        componentsWithWorkflowId.add(compositeKey);
        if (!workflowGroups.has(workflowId)) {
          workflowGroups.set(workflowId, []);
        }
        workflowGroups.get(workflowId)!.push({ compositeKey, componentId: detail.componentId, detail });
      }
    }

    for (const [compositeKey, detail] of entries) {
      const tableId = detail.additionalInfo?.tableId;
      if (tableId && !componentsWithWorkflowId.has(compositeKey) && queryViewChildTypes.has(detail.componentType)) {
        componentsWithTableId.add(compositeKey);
        if (!queryViewGroups.has(tableId)) {
          queryViewGroups.set(tableId, []);
        }
        queryViewGroups.get(tableId)!.push({ compositeKey, componentId: detail.componentId, detail });
      }
    }

    // Group ENVIRONMENT components by their source name
    for (const [compositeKey, detail] of entries) {
      if (detail.componentType === 'ENVIRONMENT') {
        const envName = detail.additionalInfo?.sourceName || detail.additionalInfo?.targetName;
        if (envName) {
          componentsInEnvironmentGroup.add(compositeKey);
          if (!environmentGroups.has(envName)) {
            environmentGroups.set(envName, []);
          }
          environmentGroups.get(envName)!.push({ compositeKey, componentId: detail.componentId, detail });
        }
      }
    }

    // Group all TECHNOLOGY_CHOICES components into a single row (one per merge, across all apps)
    for (const [compositeKey, detail] of entries) {
      if (detail.componentType === 'TECHNOLOGY_CHOICES') {
        const groupKey = 'TECHNOLOGY_CHOICES';
        componentsInTechChoicesGroup.add(compositeKey);
        if (!techChoicesGroups.has(groupKey)) {
          techChoicesGroups.set(groupKey, []);
        }
        techChoicesGroups.get(groupKey)!.push({ compositeKey, componentId: detail.componentId, detail });
      }
    }

    return { workflowGroups, queryViewGroups, environmentGroups, techChoicesGroups, componentsWithWorkflowId, componentsWithTableId, componentsInEnvironmentGroup, componentsInTechChoicesGroup };
  }

  private mergeGroupModelActionTypes(groupComponents: { detail: ComponentDetail }[]): Record<string, string> {
    const merged: Record<string, string> = {};
    for (const c of groupComponents) {
      if (c.detail.modelActionTypes) {
        Object.assign(merged, c.detail.modelActionTypes);
      }
    }
    return merged;
  }

  private processReviewGroupEntries(
    groups: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }[]>,
    allComponentsMap: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }>,
    parentIds: Set<string>,
    groupType: 'workflow' | 'queryView' | 'environment' | 'techChoices',
    userSelectedIds: Set<string>,
    result: ReviewMergeChange[]
  ): void {
    const options =
      groupType === 'queryView'
        ? { groupType: 'queryView' as const, parentComponentTypeCheck: 'QUERY_VIEW' }
        : groupType === 'environment'
          ? { groupType: 'environment' as const }
          : groupType === 'techChoices'
            ? { groupType: 'techChoices' as const }
            : { groupType: 'workflow' as const };

    for (const [groupId, groupComponents] of groups) {
      const mergedModelActionTypes = this.mergeGroupModelActionTypes(groupComponents);
      const change = this.processComponentGroup(groupComponents, groupId, allComponentsMap, parentIds, options, {
        userSelectedIds,
        mergedModelActionTypes
      }) as ReviewMergeChange;

      const depData = this.aggregateGroupDependentComponents(
        groupComponents,
        groupId,
        allComponentsMap,
        change.groupedComponentIds || []
      );
      change.dependentComponentIds = depData.ids;
      (change as any)._dependentComponentData = depData.data;
      result.push(change);
    }
  }

  private processIndividualReviewComponents(
    entries: [string, ComponentDetail][],
    components: Record<string, ComponentDetail>,
    allComponentsMap: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }>,
    skipSets: {
      componentsWithWorkflowId: Set<string>;
      workflowParentIds: Set<string>;
      componentsWithTableId: Set<string>;
      queryViewParentIds: Set<string>;
      componentsInEnvironmentGroup: Set<string>;
      componentsInTechChoicesGroup: Set<string>;
    },
    userSelectedIds: Set<string>,
    result: ReviewMergeChange[]
  ): void {
    for (const [compositeKey, detail] of entries) {
      const actualId = detail.componentId;
      if (skipSets.componentsWithWorkflowId.has(compositeKey) || skipSets.workflowParentIds.has(actualId)) {
        continue;
      }
      if (skipSets.componentsWithTableId.has(compositeKey) || skipSets.queryViewParentIds.has(actualId)) {
        continue;
      }
      if (skipSets.componentsInEnvironmentGroup.has(compositeKey)) {
        continue;
      }
      if (skipSets.componentsInTechChoicesGroup.has(compositeKey)) {
        continue;
      }

      const depData = this.resolveDependentComponentData(detail, components, allComponentsMap);
      const modelActionTypes = this.extractModelActionTypes(detail);
      const fallbackName = this.utilService.formatComponentType(detail.componentType);

      result.push({
        id: compositeKey,
        componentId: actualId,
        componentType: detail.componentType,
        recordChangeType: detail.recordChangeType,
        additionalRecordChangeTypeList: detail.additionalRecordChangeTypeList || [],
        sourceComponentName: detail.additionalInfo?.sourceName || (detail.lastSourceModifier ? fallbackName : ''),
        targetComponentName: detail.additionalInfo?.targetName || (detail.lastTargetModifier ? fallbackName : ''),
        sourceComponentPath: this.buildComponentPath(detail.componentType, detail.additionalInfo, true),
        targetComponentPath: this.buildComponentPath(detail.componentType, detail.additionalInfo, false),
        lastSourceModification: detail.lastSourceModification,
        lastSourceModifier: detail.lastSourceModifier,
        lastTargetModification: detail.lastTargetModification,
        lastTargetModifier: detail.lastTargetModifier,
        hasConflict: detail.conflicts && detail.conflicts.length > 0,
        conflicts: detail.conflicts || [],
        action: 'MERGE_SOURCE' as MergeAction,
        hasError: false,
        isAutoSelected: !userSelectedIds.has(`${detail.componentType}_${actualId}`),
        isGroupRepresentative: false,
        dependentComponentIds: depData.map((d) => d.compositeKey),
        dependentComponentNames: [],
        modelActionTypes: modelActionTypes && Object.keys(modelActionTypes).length > 0 ? modelActionTypes : undefined,
        applicationName: detail.applicationName || this.appSidToNameMap.get(detail.sourceAppId || '') || undefined,
        sourceAppId: detail.sourceAppId,
        targetAppId: detail.targetAppId,
        _dependentComponentData: depData,
        additionalInfo: detail.additionalInfo || undefined
      } as ReviewMergeChange & { _dependentComponentData?: { id: string; compositeKey: string; type: string }[] });
    }
  }

  private resolveDependentComponentData(
    detail: ComponentDetail,
    components: Record<string, ComponentDetail>,
    allComponentsMap: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }>
  ): { id: string; compositeKey: string; type: string }[] {
    const data: { id: string; compositeKey: string; type: string }[] = [];
    if (!detail.dependentComponents) {
      return data;
    }

    for (const [compType, compIds] of Object.entries(detail.dependentComponents)) {
      if (!Array.isArray(compIds)) {
        continue;
      }
      for (const id of compIds) {
        const typeSpecificKey = `${compType}_${id?.componentId}`;
        const resolvedCompositeKey = components[typeSpecificKey]
          ? typeSpecificKey
          : allComponentsMap.get(id)?.compositeKey || typeSpecificKey;
        data.push({ id, compositeKey: resolvedCompositeKey, type: this.utilService.formatComponentType(compType) });
      }
    }
    return data;
  }

  private extractModelActionTypes(detail: ComponentDetail): Record<string, string> | undefined {
    if (!detail.modelActionTypes) {
      return undefined;
    }
    return Object.entries(detail.modelActionTypes).reduce(
      (acc, [key, value]) => {
        acc[key] = value as string;
        return acc;
      },
      {} as Record<string, string>
    );
  }

  private populateDependentComponentNames(result: ReviewMergeChange[]): void {
    const componentInfoMap = new Map<string, { name: string; type: string }>();
    for (const r of result) {
      componentInfoMap.set(r.componentId, {
        name: r.sourceComponentName || r.targetComponentName || '',
        type: this.utilService.formatComponentType(r.componentType)
      });
    }

    for (const r of result) {
      const extended = r as ReviewMergeChange & { _dependentComponentData?: { id: string; type: string }[] };
      if (extended._dependentComponentData?.length) {
        r.dependentComponentNames = extended._dependentComponentData
          .map((dep) => {
            const info = componentInfoMap.get(dep.id);
            return `${info?.name || dep.id} (${dep.type})`;
          })
          .filter(Boolean);
        delete extended._dependentComponentData;
      }
    }
  }

  onSave(): void {
    if (this.activeIndex === 3) {
      this.performMerge();
    } else {
      console.log('Saving step', this.activeIndex);
    }
  }

  /**
   * Build and call the merge API
   */
  private performMerge(): void {
    if (!this.mergeId || !this.sourceBranch?.sid || !this.targetBranch?.sid) {
      console.error('Missing required data for merge operation');
      return;
    }

    // Helper function to get all component IDs, expanding workflow, query view, and environment groups
    // IMPORTANT: For synthetic rows, only include groupedComponentIds, not the synthetic componentId
    const getAllComponentIds = (changes: MergeChange[]): string[] => {
      const ids: string[] = [];
      for (const change of changes) {
        if (change.groupedComponentIds?.length) {
          ids.push(...change.groupedComponentIds);
        } else if (!change.isSyntheticWorkflow && !change.isSyntheticQueryView && !change.isSyntheticEnvironment && !change.isSyntheticTechChoices) {
          ids.push(change.componentId);
        }
      }
      return [...new Set<string>(ids)];
    };

    // Helper function to build componentsMap grouped by componentType
    const buildComponentsMap = (changes: MergeChange[]): Record<string, string[]> => {
      return this.buildSelectedComponentsMap(changes);
    };

    // Helper to build componentActionsMap (componentId -> recordChangeType)
    const buildComponentActionsMap = (changes: MergeChange[]): Record<string, string> => {
      const map: Record<string, string> = {};
      for (const change of changes) {
        if (change.groupedComponentIds?.length) {
          for (const id of change.groupedComponentIds) {
            map[id] = change.recordChangeType;
          }
        } else if (!change.isSyntheticWorkflow && !change.isSyntheticQueryView && !change.isSyntheticEnvironment && !change.isSyntheticTechChoices) {
          map[change.componentId] = change.recordChangeType;
        }
      }
      return map;
    };

    let request: MergeBranchRequest;
    if (this.isSuiteMerge) {
      request = this.buildSuiteMergeRequest(getAllComponentIds, buildComponentsMap, buildComponentActionsMap);
    } else {
      request = this.buildRegularMergeRequest(getAllComponentIds, buildComponentsMap, buildComponentActionsMap);
    }
    this.loadingReviewChanges = true;
    this.isMergeInProgress = true;

    // Call merge API - dialog stays open showing "Merge In Progress" spinner on step 4
    // Firestore listener will handle completion/failure and close the dialog
    this.repoService.mergeBranch(request).subscribe({
      next: (response: any) => {
        // API returned 200 - if Firestore listener hasn't already handled it, process the response
        if (this.isMergeInProgress) {
          const status = response?.status;
          if (status === MergeStatus.MERGE_FAILED || status === 'MERGE_FAILED') {
            this.handleMergeFailed(response?.errorMessage || '');
          } else {
            this.handleMergeCompleted(
              status === MergeStatus.MERGE_COMPLETED_WITH_PUBLISH_ERROR ||
                status === 'MERGE_COMPLETED_WITH_PUBLISH_ERROR'
            );
          }
        }
      },
      error: (error: any) => {
        // API call failed - close dialog and show failure popup
        if (this.isMergeInProgress) {
          this.handleMergeFailed(error?.error?.MESSAGE || error?.message || '');
        }
      }
    });
  }

  private addChangesToBranchGroups(
    branchGroups: Map<string, { source: MergeChange[]; dontMerge: MergeChange[]; pickTarget: MergeChange[] }>,
    changes: MergeChange[],
    targetGroup: 'source' | 'dontMerge' | 'pickTarget'
  ): void {
    const grouped = this.groupBySourceAppId(changes);
    for (const [key, groupedChanges] of grouped) {
      if (!branchGroups.has(key)) {
        branchGroups.set(key, { source: [], dontMerge: [], pickTarget: [] });
      }
      branchGroups.get(key)![targetGroup].push(...groupedChanges);
    }
  }

  private populateMemberAppDetails(memberAppDetails: Record<string, string[]>, source: MergeChange[]): void {
    for (const change of source) {
      if (change.componentType === 'APP') {
        const type = change.recordChangeType; // 'ADDED' or 'DELETED'

        if (type === 'ADDED' || type === 'DELETED') {
          // Initialize the array if it doesn't exist yet
          if (!memberAppDetails[type]) {
            memberAppDetails[type] = [];
          }
          // Push the new ID into the existing list
          memberAppDetails[type].push(change.componentId);
        }
      }
    }
  }

  /**
   * Build merge request for suite mode: selectedComponentIds is an array of per-branch component sets
   */
  private buildSuiteMergeRequest(
    getAllComponentIds: (changes: MergeChange[]) => string[],
    buildComponentsMap: (changes: MergeChange[]) => Record<string, string[]>,
    buildComponentActionsMap: (changes: MergeChange[]) => Record<string, string>
  ): MergeBranchRequest {
    // Get auto-selected component IDs

    const autoSelectedIds = new Set<string>();
    for (const c of this.reviewChanges.filter((c) => c.isAutoSelected)) {
      if (c.groupedComponentIds?.length) {
        for (const id of c.groupedComponentIds) {
          autoSelectedIds.add(id);
        }
      } else {
        autoSelectedIds.add(c.componentId);
      }
    }

    // Group all review changes by sourceAppId
    const branchGroups = new Map<
      string,
      { source: MergeChange[]; dontMerge: MergeChange[]; pickTarget: MergeChange[] }
    >();
    this.addChangesToBranchGroups(
      branchGroups,
      this.reviewChanges.filter((c) => c.action === 'MERGE_SOURCE'),
      'source'
    );
    this.addChangesToBranchGroups(
      branchGroups,
      this.allMergeChanges.filter((c) => c.action === 'MERGE_LATER'),
      'dontMerge'
    );
    this.addChangesToBranchGroups(
      branchGroups,
      this.allMergeChanges.filter((c) => c.action === 'MERGE_TARGET'),
      'pickTarget'
    );

    // Build per-branch component sets
    const branchMergeDataList: MergeBranchComponentSet[] = [];
    const allSelectedIds: string[] = [];
    const memberAppDetails: Record<string, string[]> = {};
    for (const [sourceAppId, group] of branchGroups) {
      const targetAppId =
        group.source[0]?.targetAppId ||
        group.dontMerge[0]?.targetAppId ||
        group.pickTarget[0]?.targetAppId ||
        this.targetBranch!.sid!;

      const componentsMap = buildComponentsMap(group.source);
      const componentActionsMap = buildComponentActionsMap(group.source);
      this.populateMemberAppDetails(memberAppDetails, group.source);
      // Collect all selected component IDs for this group
      allSelectedIds.push(...getAllComponentIds(group.source));

      // unselectedComponents: "Don't Merge" components excluding auto-selected
      const dontMergeIds = new Set<string>(
        getAllComponentIds(group.dontMerge).filter((id) => !autoSelectedIds.has(id))
      );
      const unselectedComponents = buildComponentsMap(
        group.dontMerge.filter((c) => {
          if (c.groupedComponentIds?.length) {
            return c.groupedComponentIds.some((id) => dontMergeIds.has(id));
          }
          return dontMergeIds.has(c.componentId);
        })
      );

      const targetselectedComponents = buildComponentsMap(group.pickTarget);

      branchMergeDataList.push({
        sourceBranchId: sourceAppId,
        targetBranchId: targetAppId,
        componentsMap,
        unselectedComponents,
        targetselectedComponents,
        componentActionsMap
      });
    }

    return {
      selectedComponentIds: [...new Set<string>(allSelectedIds)],
      branchMergeDataList,
      tagName: this.tagName.trim(),
      mergeId: this.mergeId!,
      canPublish: this.publishAfterMerge,
      memberAppDetails: memberAppDetails
    };
  }

  /**
   * Build merge request for regular (non-suite) mode - uses same array structure as suite
   */
  private buildRegularMergeRequest(
    getAllComponentIds: (changes: MergeChange[]) => string[],
    buildComponentsMap: (changes: MergeChange[]) => Record<string, string[]>,
    buildComponentActionsMap: (changes: MergeChange[]) => Record<string, string>
  ): MergeBranchRequest {
    const autoSelectedIds = new Set<string>();
    for (const c of this.reviewChanges.filter((c) => c.isAutoSelected)) {
      if (c.groupedComponentIds?.length) {
        for (const id of c.groupedComponentIds) {
          autoSelectedIds.add(id);
        }
      } else {
        autoSelectedIds.add(c.componentId);
      }
    }

    const pickSourceComponents = this.reviewChanges.filter((c) => c.action === 'MERGE_SOURCE');
    const componentsMap = buildComponentsMap(pickSourceComponents);
    const componentActionsMap = buildComponentActionsMap(pickSourceComponents);

    const dontMergeComponents = this.allMergeChanges.filter((c) => c.action === 'MERGE_LATER');
    const dontMergeComponentIds = new Set<string>(
      getAllComponentIds(dontMergeComponents).filter((id) => !autoSelectedIds.has(id))
    );
    const unselectedComponents = buildComponentsMap(
      dontMergeComponents.filter((c) => {
        if (c.groupedComponentIds?.length) {
          return c.groupedComponentIds.some((id) => dontMergeComponentIds.has(id));
        }
        return dontMergeComponentIds.has(c.componentId);
      })
    );

    const pickTargetComponents = this.allMergeChanges.filter((c) => c.action === 'MERGE_TARGET');
    const targetselectedComponents = buildComponentsMap(pickTargetComponents);

    const branchMergeDataList: MergeBranchComponentSet[] = [
      {
        sourceBranchId: this.sourceBranch!.sid!,
        targetBranchId: this.targetBranch!.sid!,
        componentsMap,
        unselectedComponents,
        targetselectedComponents,
        componentActionsMap
      }
    ];

    return {
      selectedComponentIds: getAllComponentIds(pickSourceComponents),
      branchMergeDataList,
      tagName: this.tagName.trim(),
      mergeId: this.mergeId!,
      canPublish: this.publishAfterMerge
    };
  }

  // Step 2: Load merge changes from API
  private loadMergeChanges(): void {
    if (!this.sourceBranch?.sid || !this.targetBranch?.sid) {
      return;
    }

    this.loadingChanges = true;
    this.isProcessRunning = false;
    this.processWarningMessage = '';

    // Reset pagination
    this.currentPage = 0;
    this.hasMoreData = true;
    this.allMergeChanges = [];
    this.filteredMergeChanges = [];
    this.mergeChanges = [];

    // Load merge changes directly - comparison date now comes from the API response
    this.loadMergeChangesFromApi();
  }

  /**
   * Load merge changes from API
   */
  private loadMergeChangesFromApi(): void {
    // First visit to Step 2: don't send flag (backend creates merge logs)
    // Subsequent visits: send flag as true to skip validation
    const request: MergeChangesRequest = {
      sourceBranchId: this.sourceBranch!.sid!,
      targetBranchId: this.targetBranch!.sid!
    };

    if (this.step2Visited) {
      request.ignoreOnGoingMergeValidation = true;
    }

    // Mark Step 2 as visited for subsequent navigations
    this.step2Visited = true;
    this.repoService.getLastSuccessfulMerge(request).subscribe({
      next: (response) => {
        if (response?.modifiedDate) {
          this.comparisonDate = response.modifiedDate;
          this.noMergesDone = false;
        } else {
          // If API doesn't return lastMergedDate, fall back to source branch created date
          this.setComparisonDateFromSourceBranchCreatedDate();
        }
      },
      error: () => {
        // If API call fails, fall back to source branch created date for comparison
        this.setComparisonDateFromSourceBranchCreatedDate();
      }
    });
    this.repoService.getBranchMergeChanges(request).subscribe({
      next: (response) => {
        // Response can be an array (suite merge) or single object (regular merge)
        const responseArray = Array.isArray(response) ? response : [response];

        // Merge all componentChangeDetails from each element and extract mergeId
        const mergedComponentChangeDetails = this.mergeComponentChangeDetails(responseArray);

        // Build component name lookup for path resolution
        this.buildComponentNameLookup(mergedComponentChangeDetails as any);

        // Transform API response to MergeChange array
        this.allMergeChanges = this.transformApiResponse(mergedComponentChangeDetails as any);

        // Apply initial sort
        this.sortAllChanges();

        // Update component type filter options
        this.componentTypeOptions = this.getUniqueComponentTypes();
        this.modifiedByOptions = this.getUniqueModifiedBy();

        // Build application filter options for suite merges
        if (this.isSuiteMerge) {
          this.buildApplicationOptionsForSuite();
        }

        // Apply filters and load first page
        this.applyFiltersAndSort();

        // Attach scroll listener to PrimeNG's internal scroll container
        this.attachTableScrollListener();

        // Cache the payload for future comparison
        this.lastGetChangesPayload = {
          sourceBranchId: request.sourceBranchId,
          targetBranchId: request.targetBranchId
        };

        this.loadingChanges = false;
      },
      error: (error) => {
        console.error('Error loading merge changes:', error);
        this.isProcessRunning = true;
        // Handle different response formats (MESSAGE or message)
        const errorBody = error?.error;
        this.processWarningMessage =
          errorBody?.MESSAGE || errorBody?.message || this.translate.instant('FAILED_TO_LOAD_MERGE_CHANGES');
        this.loadingChanges = false;
      }
    });
  }

  /**
   * Merges componentChangeDetails from each response element and extracts mergeId/suiteMerge flags.
   */
  private mergeComponentChangeDetails(responseArray: any[]): Record<string, Record<string, any>> {
    const merged: Record<string, Record<string, any>> = {};
    for (const item of responseArray) {
      if (item.mergeId && !this.mergeId) {
        this.mergeId = item.mergeId;
        this.mergeHistoryTrackerId = item.mergeId;
        this.subscribeToMergeStatus();
      }
      if (item.suiteMerge) {
        this.isSuiteMerge = true;
      }
      const details = item.componentChangeDetails || {};
      for (const [componentType, components] of Object.entries(details)) {
        if (!merged[componentType]) {
          merged[componentType] = {};
        }
        Object.assign(merged[componentType], components as any);
      }
    }
    return merged;
  }

  /** Builds the application filter options for suite merges from allMergeChanges. */
  private buildApplicationOptionsForSuite(): void {
    const appNames = new Set<string>();
    for (const change of this.allMergeChanges) {
      if (change.applicationName) {
        appNames.add(change.applicationName);
      }
    }
    this.applicationOptions = Array.from(appNames).map((name) => ({ label: name, value: name }));
  }

  /**
   * Fallback comparison date when no successful merge record exists.
   */
  private setComparisonDateFromSourceBranchCreatedDate(): void {
    const branchData = this.sourceBranchFromApi || this.sourceBranch;
    if (branchData?.branchCreatedDate || branchData?.createdDate || branchData?.createdOn) {
      this.comparisonDate = branchData.branchCreatedDate || branchData.createdDate || branchData.createdOn!;
      this.noMergesDone = true;
      return;
    }
    this.comparisonDate = new Date();
    this.noMergesDone = true;
  }

  /**
   * Flatten nested API response structure.
   * Format: Map<ReferenceModelType, Map<String, ComponentDetail>>
   * This method flattens it to: Map<String, ComponentDetail>
   * Uses composite key (componentType_componentId) to handle same componentId in multiple ReferenceModelType maps.
   */
  private flattenComponentChangeDetails(
    nestedComponents: Record<string, Record<string, ComponentDetail>>
  ): Record<string, ComponentDetail> {
    const flatComponents: Record<string, ComponentDetail> = {};

    if (!nestedComponents) {
      return flatComponents;
    }

    for (const [referenceModelType, componentTypeMap] of Object.entries(nestedComponents)) {
      if (!componentTypeMap || typeof componentTypeMap !== 'object') {
        continue;
      }
      this.flattenSingleTypeMap(referenceModelType, componentTypeMap, flatComponents);
    }

    return flatComponents;
  }

  private flattenSingleTypeMap(
    referenceModelType: string,
    componentTypeMap: Record<string, ComponentDetail>,
    flatComponents: Record<string, ComponentDetail>
  ): void {
    for (const [componentId, detail] of Object.entries(componentTypeMap)) {
      if (!detail) {
        continue;
      }
      if (!detail.componentId) {
        detail.componentId = componentId;
      }
      detail.componentType = referenceModelType;
      flatComponents[`${referenceModelType}_${componentId}`] = detail;
    }
  }

  /**
   * Build a lookup map from componentId to component names.
   * This is used to resolve parent names when building component paths.
   */
  private buildComponentNameLookup(nestedComponents: Record<string, Record<string, ComponentDetail>>): void {
    this.componentNameLookup.clear();

    if (!nestedComponents) {
      return;
    }

    for (const [referenceModelType, componentTypeMap] of Object.entries(nestedComponents)) {
      if (componentTypeMap && typeof componentTypeMap === 'object') {
        for (const [componentId, detail] of Object.entries(componentTypeMap)) {
          if (!detail) {
            continue;
          }
          const id = detail.componentId || componentId;
          this.componentNameLookup.set(id, {
            sourceName: detail.additionalInfo?.sourceName || '',
            targetName: detail.additionalInfo?.targetName || '',
            componentType: referenceModelType
          });
        }
      }
    }
  }

  /**
   * Build the component path based on component type and additionalInfo.
   * Returns a path like "TableName" for child components.
   *
   * @param componentType - The type of the component (e.g., 'FIELD', 'TABLE_INDEX')
   * @param additionalInfo - The additionalInfo from the component detail
   * @param isSource - Whether to build source path (true) or target path (false)
   */
  private buildComponentPath(
    componentType: string,
    additionalInfo: ComponentAdditionalInfo | undefined,
    isSource: boolean
  ): string {
    if (!additionalInfo) {
      return '';
    }

    // Components that need parent path lookup
    const parentIdField = this.getParentIdField(componentType);
    if (!parentIdField) {
      return '';
    }

    // For table children (FIELD, etc.), try to get table name directly from additionalInfo first
    if (parentIdField === 'tableId') {
      const tableName = isSource
        ? ((additionalInfo as Record<string, unknown>)['sourceTableName'] as string)
        : ((additionalInfo as Record<string, unknown>)['targetTableName'] as string);

      if (tableName) {
        return `Table: ${tableName}`;
      }
    }

    const parentId = (additionalInfo as Record<string, unknown>)[parentIdField] as string;
    if (!parentId) {
      return '';
    }

    // Fallback: Look up the parent name from component lookup
    const parentInfo = this.componentNameLookup.get(parentId);
    if (!parentInfo) {
      return '';
    }

    const parentName = isSource ? parentInfo.sourceName : parentInfo.targetName;
    if (!parentName) {
      return '';
    }

    // Format the path with parent type prefix
    const parentTypeLabel = this.getParentTypeLabel(componentType, parentInfo.componentType);
    return parentTypeLabel ? `${parentTypeLabel}: ${parentName}` : parentName;
  }

  /**
   * Get the additionalInfo field name that contains the parent ID for a component type.
   */
  private getParentIdField(componentType: string): string | null {
    // Components grouped under tables
    const tableChildComponents = [
      'FIELD',
      'FIELD_GROUP',
      'TABLE_INDEX',
      'TABLE_FIELD_ACL',
      'FOREIGN_KEY',
      'LOOKUP',
      'LOOKUP_TEMPLATE',
      'SERVICE',
      'RUNTIME_CONFIGURATION',
      'QUERY_VIEW_CUD',
      'SEARCHABLE_INDEX_FIELD'
    ];

    // Components grouped under workflows
    const workflowChildComponents = ['WORKFLOW_ACTOR_STEP', 'WORKFLOW_ACTION_STEP', 'WORKFLOW_MAIL_INFO'];

    // Components grouped under query views (using tableId as the query view ID)
    const queryViewChildComponents = ['QUERY_VIEW_FIELD', 'QUERYVIEW_CUD_CONFIGURATION'];

    if (tableChildComponents.includes(componentType)) {
      return 'tableId';
    }

    if (workflowChildComponents.includes(componentType)) {
      return 'workflowId';
    }

    if (queryViewChildComponents.includes(componentType)) {
      return 'tableId';
    }

    return null;
  }

  /**
   * Get a human-readable label for the parent component type.
   */
  private getParentTypeLabel(childComponentType: string, parentComponentType: string): string {
    // Map parent component types to readable labels
    const typeLabels: Record<string, string> = {
      TABLE: 'Table',
      WORKFLOW: 'Workflow',
      QUERY_VIEW: 'Query View'
    };

    // Try exact match first
    if (typeLabels[parentComponentType]) {
      return typeLabels[parentComponentType];
    }

    // Try case-insensitive match
    const upperParent = parentComponentType?.toUpperCase();
    if (typeLabels[upperParent]) {
      return typeLabels[upperParent];
    }

    // Derive parent type from child component type as fallback
    const tableChildComponents = [
      'FIELD',
      'FIELD_GROUP',
      'TABLE_INDEX',
      'TABLE_FIELD_ACL',
      'FOREIGN_KEY',
      'LOOKUP',
      'LOOKUP_TEMPLATE',
      'SERVICE',
      'RUNTIME_CONFIGURATION',
      'SEARCHABLE_INDEX_FIELD'
    ];
    const workflowChildComponents = ['WORKFLOW_ACTOR_STEP', 'WORKFLOW_ACTION_STEP', 'WORKFLOW_MAIL_INFO'];
    const queryViewChildComponents = ['QUERY_VIEW_FIELD', 'QUERY_VIEW_CUD', 'QUERYVIEW_CUD_CONFIGURATION'];

    if (tableChildComponents.includes(childComponentType)) {
      return 'Table';
    }
    if (workflowChildComponents.includes(childComponentType)) {
      return 'Workflow';
    }
    if (queryViewChildComponents.includes(childComponentType)) {
      return 'Query View';
    }

    return '';
  }

  /**
   * Helper method to process workflow or query view component groups.
   * Reduces duplication between workflow and query view group processing.
   *
   * @param groupComponents - Array of components in the group
   * @param groupId - The workflow ID or table ID
   * @param allComponentsMap - Map of all components for parent lookup
   * @param parentIds - Set to track used parent IDs (modified in place)
   * @param config - Configuration specifying group type and optional parent type check
   * @param reviewOptions - Optional parameters for ReviewMergeChange (userSelectedIds for auto-select check, mergedModelActionTypes)
   */
  private processComponentGroup(
    groupComponents: { compositeKey: string; componentId: string; detail: ComponentDetail }[],
    groupId: string,
    allComponentsMap: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }>,
    parentIds: Set<string>,
    config: { groupType: 'workflow' | 'queryView' | 'environment' | 'techChoices'; parentComponentTypeCheck?: string },
    reviewOptions?: { userSelectedIds: Set<string>; mergedModelActionTypes: Record<string, string> }
  ): MergeChange | ReviewMergeChange {
    const groupedIds = groupComponents.map((c) => c.componentId);
    const groupedComponentTypes = this.buildGroupedComponentTypes(groupComponents);
    const parentComponent = allComponentsMap.get(groupId);
    const isValidParent = this.isParentComponentValid(parentComponent, config.parentComponentTypeCheck);
    const groupConfig = this.getGroupTypeConfig(config.groupType);
    const hasParent = isValidParent && !!parentComponent;

    if (hasParent && parentComponent) {
      this.registerParentInGroup(groupId, parentComponent, groupedIds, groupedComponentTypes, parentIds);
    }

    const componentName =
      hasParent && parentComponent
        ? parentComponent.detail.additionalInfo?.sourceName || ''
        : this.resolveSyntheticGroupName(groupComponents[0], groupConfig.nameField, groupId);

    const sourceComponent =
      hasParent && parentComponent
        ? parentComponent
        : {
            compositeKey: `${groupConfig.syntheticIdPrefix}${groupId}`,
            componentId: groupId,
            detail: groupComponents[0].detail
          };

    const baseResult = this.buildGroupBaseResult(
      hasParent ? parentComponent : null,
      sourceComponent,
      groupComponents,
      { groupId, componentName, groupedIds, groupedComponentTypes, groupConfig }
    );

    // For environment groups, compute consensus recordChangeType from members instead of default 'MODIFIED'
    if (config.groupType === 'environment' && !hasParent) {
      baseResult.recordChangeType = this.resolveGroupRecordChangeType(groupComponents);
    }

    // For tech choices groups, compute consensus recordChangeType and use the formatted type name as display name
    if (config.groupType === 'techChoices' && !hasParent) {
      baseResult.recordChangeType = this.resolveGroupRecordChangeType(groupComponents);
      const tcDisplayName = this.utilService.formatComponentType('TECHNOLOGY_CHOICES');
      baseResult.sourceComponentName = tcDisplayName;
      baseResult.targetComponentName = tcDisplayName;
    }

    if (!reviewOptions) {
      return baseResult;
    }

    return this.applyReviewOptions(
      baseResult,
      groupComponents,
      groupId,
      hasParent ? parentComponent : null,
      reviewOptions
    );
  }

  private buildGroupedComponentTypes(
    groupComponents: { componentId: string; detail: ComponentDetail }[]
  ): Record<string, string> {
    const types: Record<string, string> = {};
    for (const c of groupComponents) {
      types[c.componentId] = c.detail.componentType;
    }
    return types;
  }

  private isParentComponentValid(
    parentComponent: { detail: ComponentDetail } | undefined,
    parentComponentTypeCheck?: string
  ): boolean {
    if (!parentComponent) return false;
    if (!parentComponentTypeCheck) return true;
    return parentComponent.detail.componentType === parentComponentTypeCheck;
  }

  private getGroupTypeConfig(groupType: 'workflow' | 'queryView' | 'environment' | 'techChoices'): {
    syntheticIdPrefix: string;
    syntheticComponentType: string;
    nameField: string;
    idField: string;
    syntheticFlag: string;
  } {
    if (groupType === 'techChoices') {
      return {
        syntheticIdPrefix: 'synthetic_tc_',
        syntheticComponentType: 'TECHNOLOGY_CHOICES',
        nameField: 'techChoicesName',
        idField: 'techChoicesId',
        syntheticFlag: 'isSyntheticTechChoices'
      };
    }
    if (groupType === 'environment') {
      return {
        syntheticIdPrefix: 'synthetic_env_',
        syntheticComponentType: 'ENVIRONMENT',
        nameField: 'environmentName',
        idField: 'environmentName',
        syntheticFlag: 'isSyntheticEnvironment'
      };
    }
    const isWorkflow = groupType === 'workflow';
    return {
      syntheticIdPrefix: isWorkflow ? 'synthetic_' : 'synthetic_qv_',
      syntheticComponentType: isWorkflow ? 'WORKFLOW' : 'QUERY_VIEW',
      nameField: isWorkflow ? 'workflowName' : 'queryViewName',
      idField: isWorkflow ? 'workflowId' : 'tableId',
      syntheticFlag: isWorkflow ? 'isSyntheticWorkflow' : 'isSyntheticQueryView'
    };
  }

  private resolveGroupRecordChangeType(groupComponents: { detail: ComponentDetail }[]): RecordChangeType {
    const types = new Set(groupComponents.map((c) => c.detail.recordChangeType));
    return types.size === 1 ? ([...types][0] as RecordChangeType) : 'MODIFIED';
  }

  private registerParentInGroup(
    groupId: string,
    parentComponent: { detail: ComponentDetail },
    groupedIds: string[],
    groupedComponentTypes: Record<string, string>,
    parentIds: Set<string>
  ): void {
    if (!groupedIds.includes(groupId)) {
      groupedIds.push(groupId);
    }
    groupedComponentTypes[groupId] = parentComponent.detail.componentType;
    parentIds.add(groupId);
  }

  private resolveSyntheticGroupName(
    firstComponent: { detail: ComponentDetail },
    nameField: string,
    groupId: string
  ): string {
    const lookupNames = this.componentNameLookup.get(groupId);
    return (
      (firstComponent.detail.additionalInfo as any)?.[nameField] ||
      firstComponent.detail.additionalInfo?.sourceName ||
      lookupNames?.sourceName ||
      lookupNames?.targetName ||
      this.getAppMetaDisplayName(groupId) ||
      groupId
    );
  }

  private collectGroupConflicts(
    groupComponents: { detail: ComponentDetail }[],
    parentComponent: { detail: ComponentDetail } | null
  ): { hasConflict: boolean; conflicts: any[] } {
    const childConflicts = groupComponents.flatMap((c) => c.detail.conflicts || []);
    const parentConflicts = parentComponent?.detail.conflicts || [];
    const allConflicts = [...childConflicts, ...parentConflicts];
    return { hasConflict: allConflicts.length > 0, conflicts: allConflicts };
  }

  private buildGroupBaseResult(
    parentComponent: { compositeKey: string; componentId: string; detail: ComponentDetail } | null,
    sourceComponent: { compositeKey: string; componentId: string; detail: ComponentDetail },
    groupComponents: { compositeKey: string; componentId: string; detail: ComponentDetail }[],
    options: {
      groupId: string;
      componentName: string;
      groupedIds: string[];
      groupedComponentTypes: Record<string, string>;
      groupConfig: {
        syntheticIdPrefix: string;
        syntheticComponentType: string;
        nameField: string;
        idField: string;
        syntheticFlag: string;
      };
    }
  ): MergeChange {
    const { groupId, componentName, groupedIds, groupedComponentTypes, groupConfig } = options;
    const hasParent = !!parentComponent;
    const conflictInfo = this.collectGroupConflicts(groupComponents, parentComponent);

    return {
      id: hasParent ? parentComponent.compositeKey : `${groupConfig.syntheticIdPrefix}${groupId}`,
      componentId: hasParent ? parentComponent.componentId : groupId,
      componentType: hasParent ? parentComponent.detail.componentType : groupConfig.syntheticComponentType,
      recordChangeType: hasParent ? parentComponent.detail.recordChangeType : 'MODIFIED',
      additionalRecordChangeTypeList: hasParent ? parentComponent.detail.additionalRecordChangeTypeList || [] : [],
      sourceComponentName: componentName,
      targetComponentName: hasParent ? parentComponent.detail.additionalInfo?.targetName || '' : componentName,
      sourceComponentPath: hasParent
        ? this.buildComponentPath(parentComponent.detail.componentType, parentComponent.detail.additionalInfo, true)
        : '',
      targetComponentPath: hasParent
        ? this.buildComponentPath(parentComponent.detail.componentType, parentComponent.detail.additionalInfo, false)
        : '',
      lastSourceModification: sourceComponent.detail.lastSourceModification,
      lastSourceModifier: sourceComponent.detail.lastSourceModifier,
      lastTargetModification: sourceComponent.detail.lastTargetModification,
      lastTargetModifier: sourceComponent.detail.lastTargetModifier,
      hasConflict: conflictInfo.hasConflict,
      conflicts: conflictInfo.conflicts,
      action: 'MERGE_SOURCE' as MergeAction,
      hasError: false,
      [groupConfig.idField]: groupId,
      groupedComponentIds: groupedIds,
      groupedComponentTypes: groupedComponentTypes,
      isGroupRepresentative: true,
      [groupConfig.syntheticFlag]: !hasParent,
      applicationName:
        sourceComponent.detail.applicationName ||
        this.appSidToNameMap.get(sourceComponent.detail.sourceAppId || '') ||
        undefined,
      sourceAppId: sourceComponent.detail.sourceAppId,
      targetAppId: sourceComponent.detail.targetAppId,
      ...(hasParent ? {} : { [groupConfig.nameField]: componentName }),
      ...(groupConfig.syntheticFlag === 'isSyntheticEnvironment' || groupConfig.syntheticFlag === 'isSyntheticTechChoices'
        ? { groupedComponentAppIds: this.buildGroupedComponentAppIds(groupComponents) }
        : {})
    };
  }

  private buildGroupedComponentAppIds(
    groupComponents: { componentId: string; detail: ComponentDetail }[]
  ): Record<string, { sourceAppId: string; targetAppId: string }> {
    const map: Record<string, { sourceAppId: string; targetAppId: string }> = {};
    for (const c of groupComponents) {
      if (c.detail.sourceAppId) {
        map[c.componentId] = {
          sourceAppId: c.detail.sourceAppId,
          targetAppId: c.detail.targetAppId || ''
        };
      }
    }
    return map;
  }

  private applyReviewOptions(
    baseResult: MergeChange,
    groupComponents: { componentId: string; detail: ComponentDetail }[],
    groupId: string,
    parentComponent: { detail: ComponentDetail } | null,
    reviewOptions: { userSelectedIds: Set<string>; mergedModelActionTypes: Record<string, string> }
  ): ReviewMergeChange {
    const reviewResult = baseResult as ReviewMergeChange;
    const childAutoSelected = !groupComponents.some((c) =>
      reviewOptions.userSelectedIds.has(`${c.detail.componentType}_${c.componentId}`)
    );

    const parentAutoSelected = parentComponent
      ? !reviewOptions.userSelectedIds.has(`${parentComponent.detail.componentType}_${groupId}`)
      : true;

    reviewResult.isAutoSelected = childAutoSelected && parentAutoSelected;

    if (parentComponent?.detail.modelActionTypes) {
      for (const [key, value] of Object.entries(parentComponent.detail.modelActionTypes)) {
        reviewOptions.mergedModelActionTypes[key] = value as string;
      }
    }

    if (Object.keys(reviewOptions.mergedModelActionTypes).length > 0) {
      reviewResult.modelActionTypes = { ...reviewOptions.mergedModelActionTypes };
    }

    return reviewResult;
  }

  /**
   * Transform API response to MergeChange array
   * Groups components by workflowId if present.
   * Groups components by tableId for Query View if present.
   *
   * API response format: Map<ReferenceModelType, Map<String, ComponentDetail>>
   */
  private transformApiResponse(nestedComponents: Record<string, Record<string, ComponentDetail>>): MergeChange[] {
    const components = this.flattenComponentChangeDetails(nestedComponents);
    const entries = Object.entries(components);
    const allComponentsMap = this.buildAllComponentsMap(entries);
    const grouping = this.categorizeComponentGroups(entries);

    const result: MergeChange[] = [];
    const workflowParentIds = new Set<string>();
    const queryViewParentIds = new Set<string>();
    const environmentParentIds = new Set<string>();
    const techChoicesParentIds = new Set<string>();

    for (const [workflowId, groupComponents] of grouping.workflowGroups) {
      result.push(
        this.processComponentGroup(groupComponents, workflowId, allComponentsMap, workflowParentIds, {
          groupType: 'workflow'
        })
      );
    }

    for (const [tableId, groupComponents] of grouping.queryViewGroups) {
      result.push(
        this.processComponentGroup(groupComponents, tableId, allComponentsMap, queryViewParentIds, {
          groupType: 'queryView',
          parentComponentTypeCheck: 'QUERY_VIEW'
        })
      );
    }

    for (const [envName, groupComponents] of grouping.environmentGroups) {
      result.push(
        this.processComponentGroup(groupComponents, envName, allComponentsMap, environmentParentIds, {
          groupType: 'environment'
        })
      );
    }

    for (const [appId, groupComponents] of grouping.techChoicesGroups) {
      result.push(
        this.processComponentGroup(groupComponents, appId, allComponentsMap, techChoicesParentIds, {
          groupType: 'techChoices'
        })
      );
    }

    this.processIndividualMergeComponents(
      entries,
      grouping.componentsWithWorkflowId,
      workflowParentIds,
      grouping.componentsWithTableId,
      queryViewParentIds,
      grouping.componentsInEnvironmentGroup,
      grouping.componentsInTechChoicesGroup,
      result
    );

    return result;
  }

  private processIndividualMergeComponents(
    entries: [string, ComponentDetail][],
    componentsWithWorkflowId: Set<string>,
    workflowParentIds: Set<string>,
    componentsWithTableId: Set<string>,
    queryViewParentIds: Set<string>,
    componentsInEnvironmentGroup: Set<string>,
    componentsInTechChoicesGroup: Set<string>,
    result: MergeChange[]
  ): void {
    for (const [compositeKey, detail] of entries) {
      const actualId = detail.componentId;
      if (componentsWithWorkflowId.has(compositeKey) || workflowParentIds.has(actualId)) {
        continue;
      }
      if (componentsWithTableId.has(compositeKey) || queryViewParentIds.has(actualId)) {
        continue;
      }
      if (componentsInEnvironmentGroup.has(compositeKey)) {
        continue;
      }
      if (componentsInTechChoicesGroup.has(compositeKey)) {
        continue;
      }

      const fallbackName = this.utilService.formatComponentType(detail.componentType);
      result.push({
        id: compositeKey,
        componentId: actualId,
        componentType: detail.componentType,
        recordChangeType: detail.recordChangeType,
        additionalRecordChangeTypeList: detail.additionalRecordChangeTypeList || [],
        sourceComponentName: detail.additionalInfo?.sourceName || (detail.lastSourceModifier ? fallbackName : ''),
        targetComponentName: detail.additionalInfo?.targetName || (detail.lastTargetModifier ? fallbackName : ''),
        sourceComponentPath: this.buildComponentPath(detail.componentType, detail.additionalInfo, true),
        targetComponentPath: this.buildComponentPath(detail.componentType, detail.additionalInfo, false),
        lastSourceModification: detail.lastSourceModification,
        lastSourceModifier: detail.lastSourceModifier,
        lastTargetModification: detail.lastTargetModification,
        lastTargetModifier: detail.lastTargetModifier,
        hasConflict: detail.conflicts && detail.conflicts.length > 0,
        conflicts: detail.conflicts || [],
        action: 'MERGE_SOURCE' as MergeAction,
        hasError: false,
        isGroupRepresentative: false,
        applicationName: detail.applicationName || this.appSidToNameMap.get(detail.sourceAppId || '') || undefined,
        sourceAppId: detail.sourceAppId,
        targetAppId: detail.targetAppId
      });
    }
  }

  private getUniqueComponentTypes(): FilterOption[] {
    const types = [...new Set<string>(this.allMergeChanges.map((c) => c.componentType))].filter((t) => t !== 'RUNTIME_CONFIGURATION');
    return types.map((t) => ({ label: this.utilService.formatComponentType(t), value: t }));
  }

  private getUniqueModifiedBy(): FilterOption[] {
    const modifiers = [
      ...new Set<string>(this.allMergeChanges.map((c) => c.lastSourceModifier).filter((m): m is string => !!m))
    ];
    return modifiers
      .map((m) => ({ label: this.getUserDisplayName(m), value: m }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Apply filters and sorting, then reset pagination
   */
  private applyFiltersAndSort(): void {
    // Start with all changes
    let filtered = [...this.allMergeChanges];
    // Hide RUNTIME_CONFIGURATION from UI display (still included in merge payload)
    filtered = filtered.filter((c) => c.componentType !== 'RUNTIME_CONFIGURATION');

    // Apply component type filter (safely check for null/undefined)
    if (this.filterComponentTypes?.length > 0) {
      filtered = filtered.filter((c) => this.filterComponentTypes.includes(c.componentType));
    }

    // Apply change type filter (safely check for null/undefined)
    if (this.filterChangeTypes?.length > 0) {
      filtered = filtered.filter((c) => {
        const additionalTypes = c.additionalRecordChangeTypeList || [];
        const isRenamedAndModified = additionalTypes.includes('RENAMED') && additionalTypes.includes('MODIFIED');
        return this.filterChangeTypes.some((filterType) => {
          if (filterType === 'RENAMED_AND_MODIFIED') {
            return isRenamedAndModified;
          }
          return c.recordChangeType === filterType && !isRenamedAndModified;
        });
      });
    }

    // Apply action type filter (safely check for null/undefined)
    if (this.filterActionTypes?.length > 0) {
      filtered = filtered.filter((c) => this.filterActionTypes.includes(c.action));
    }

    // Apply modified by filter (safely check for null/undefined)
    if (this.filterModifiedBy?.length > 0) {
      filtered = filtered.filter((c) => c.lastSourceModifier && this.filterModifiedBy.includes(c.lastSourceModifier));
    }

    // Apply conflict filter (safely check for null/undefined)
    if (this.filterConflicts?.length > 0) {
      const showConflicts = this.filterConflicts.includes('YES');
      const showNoConflicts = this.filterConflicts.includes('NO');
      if (showConflicts && !showNoConflicts) {
        filtered = filtered.filter((c) => c.hasConflict || c.hasError);
      } else if (!showConflicts && showNoConflicts) {
        filtered = filtered.filter((c) => !c.hasConflict && !c.hasError);
      }
      // If both YES and NO are selected, show all (no filter applied)
    }
    // Application filter (only if suite merge)
    if (this.isSuiteMerge && this.filterApplications.length > 0) {
      filtered = filtered.filter((c) => {
        const appName = (c as any).applicationName as string | undefined;
        return appName && this.filterApplications.includes(appName);
      });
    }

    // Sort the filtered results
    filtered.sort((a, b) => {
      const aVal = this.getNestedValue(a, this.sortField);
      const bVal = this.getNestedValue(b, this.sortField);

      if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    this.filteredMergeChanges = filtered;

    // Reset pagination and load first page
    this.currentPage = 0;
    this.hasMoreData = true;
    this.mergeChanges = [];
    this.loadNextPage();

    // Update header checkbox states based on filtered results
    this.updateHeaderCheckboxStates();
  }

  /**
   * Load next page of data for infinite scroll
   */
  loadNextPage(): void {
    if (this.loadingMore || !this.hasMoreData) {
      return;
    }

    this.loadingMore = true;

    const startIndex = this.currentPage * this.PAGE_SIZE;
    const endIndex = startIndex + this.PAGE_SIZE;
    const nextPageData = this.filteredMergeChanges.slice(startIndex, endIndex);

    if (nextPageData.length > 0) {
      this.mergeChanges = [...this.mergeChanges, ...nextPageData];
      this.currentPage++;
    }

    // Check if there's more data
    if (endIndex >= this.filteredMergeChanges.length) {
      this.hasMoreData = false;
    }

    this.loadingMore = false;
  }

  /**
   * Handle scroll event for infinite scroll
   */
  onTableScroll(event: any): void {
    const element = event.target;
    const threshold = 100; // pixels from bottom to trigger load

    if (element.scrollHeight - element.scrollTop - element.clientHeight < threshold) {
      this.ngZone.run(() => this.loadNextPage());
    }
  }

  /**
   * Attach scroll listener to PrimeNG table's internal scroll container.
   */
  private attachTableScrollListener(): void {
    this.detachTableScrollListener();

    // Wait for Angular to render the table after data loads
    this.scrollAttachTimeout = setTimeout(() => {
      const scrollContainer = this.elRef.nativeElement.querySelector(
        '.merge-changes-table .p-datatable-table-container'
      );
      if (scrollContainer) {
        this.ngZone.runOutsideAngular(() => {
          this.scrollListenerCleanup = this.renderer.listen(scrollContainer, 'scroll', (event: Event) =>
            this.onTableScroll(event)
          );
        });
      }
    });
  }

  /**
   * Detach the programmatically attached scroll listener
   */
  private detachTableScrollListener(): void {
    if (this.scrollAttachTimeout) {
      clearTimeout(this.scrollAttachTimeout);
      this.scrollAttachTimeout = null;
    }
    if (this.scrollListenerCleanup) {
      this.scrollListenerCleanup();
      this.scrollListenerCleanup = null;
    }
  }

  /**
   * Sort all changes (used when sort changes)
   */
  private sortAllChanges(): void {
    this.allMergeChanges.sort((a, b) => {
      const aVal = this.getNestedValue(a, this.sortField);
      const bVal = this.getNestedValue(b, this.sortField);

      if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Step 2: Filter methods
  onFilterChange(): void {
    // Handle null values from clear icon (p-multiSelect sets null when cleared)
    this.filterApplications ??= [];
    this.filterComponentTypes ??= [];
    this.filterChangeTypes ??= [];
    this.filterActionTypes ??= [];
    // Auto-select scope based on filter state
    if (this.hasActiveFilters) {
      this.applyToScope = 'filtered';
    } else {
      this.applyToScope = 'all';
    }
    // Re-apply filters and reset pagination
    this.applyFiltersAndSort();
  }

  clearFilters(): void {
    this.filterApplications = [];
    this.filterComponentTypes = [];
    this.filterChangeTypes = [];
    this.filterActionTypes = [];
    this.filterModifiedBy = [];
    this.filterConflicts = [];
    this.applyToScope = 'all';
    this.applyFiltersAndSort();
  }

  /**
   * Open filter dialog with current filter values
   */
  openFilterDialog(): void {
    this.tempFilterComponentTypes = [...this.filterComponentTypes];
    this.tempFilterChangeTypes = [...this.filterChangeTypes];
    this.tempFilterActionTypes = [...this.filterActionTypes];
    this.tempFilterConflicts = [...this.filterConflicts];
    this.showFilterDialog = true;
  }

  /**
   * Apply filters from dialog
   */
  applyFilterDialog(): void {
    this.filterComponentTypes = [...this.tempFilterComponentTypes];
    this.filterChangeTypes = [...this.tempFilterChangeTypes];
    this.filterActionTypes = [...this.tempFilterActionTypes];
    this.filterConflicts = [...this.tempFilterConflicts];
    this.showFilterDialog = false;

    // Auto-select scope based on filter state
    if (this.hasActiveFilters) {
      this.applyToScope = 'filtered';
    } else {
      this.applyToScope = 'all';
    }

    this.applyFiltersAndSort();
  }

  /**
   * Cancel filter dialog
   */
  cancelFilterDialog(): void {
    this.showFilterDialog = false;
  }

  /**
   * Clear all filters in dialog
   */
  clearFilterDialog(): void {
    this.tempFilterComponentTypes = [];
    this.tempFilterChangeTypes = [];
    this.tempFilterActionTypes = [];
    this.tempFilterConflicts = [];
  }

  /**
   * Called when filter popover is shown - initialize temp values
   */
  onFilterPopoverShow(): void {
    this.tempFilterComponentTypes = [...this.filterComponentTypes];
    this.tempFilterChangeTypes = [...this.filterChangeTypes];
    this.tempFilterActionTypes = [...this.filterActionTypes];
    this.tempFilterConflicts = [...this.filterConflicts];
  }

  /**
   * Apply filters from popover
   */
  applyFilterPopover(popover: any): void {
    this.filterComponentTypes = [...this.tempFilterComponentTypes];
    this.filterChangeTypes = [...this.tempFilterChangeTypes];
    this.filterActionTypes = [...this.tempFilterActionTypes];
    this.filterConflicts = [...this.tempFilterConflicts];
    popover.hide();

    // Auto-select scope based on filter state
    if (this.hasActiveFilters) {
      this.applyToScope = 'filtered';
    } else {
      this.applyToScope = 'all';
    }

    this.applyFiltersAndSort();
  }

  /**
   * Clear all filters in popover
   */
  clearFilterPopover(): void {
    this.clearFilterDialog();
  }

  /**
   * Remove a specific filter chip
   */
  removeFilter(type: 'component' | 'change' | 'action' | 'conflict', value: string): void {
    if (type === 'component') {
      this.filterComponentTypes = this.filterComponentTypes.filter((v) => v !== value);
    } else if (type === 'change') {
      this.filterChangeTypes = this.filterChangeTypes.filter((v) => v !== value);
    } else if (type === 'action') {
      this.filterActionTypes = this.filterActionTypes.filter((v) => v !== value);
    } else if (type === 'conflict') {
      this.filterConflicts = this.filterConflicts.filter((v) => v !== value);
    }

    // Reset scope to 'all' if no filters remain
    if (!this.hasActiveFilters) {
      this.applyToScope = 'all';
    }

    this.applyFiltersAndSort();
  }

  /**
   * Get label for a filter value
   */
  getFilterLabel(type: 'component' | 'change' | 'action' | 'conflict', value: string): string {
    if (type === 'component') {
      return this.componentTypeOptions.find((o) => o.value === value)?.label || value;
    } else if (type === 'change') {
      return this.changeTypeOptions.find((o) => o.value === value)?.label || value;
    } else if (type === 'conflict') {
      return this.conflictOptions.find((o) => o.value === value)?.label || value;
    } else {
      return this.actionTypeOptions.find((o) => o.value === value)?.label || value;
    }
  }

  /**
   * Get total active filter count
   */
  get activeFilterCount(): number {
    return (
      (this.filterComponentTypes?.length || 0) +
      (this.filterChangeTypes?.length || 0) +
      (this.filterActionTypes?.length || 0) +
      (this.filterConflicts?.length || 0)
    );
  }

  // Step 2: Sorting
  onSort(field: string): void {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortOrder = 'asc';
    }
    // Re-apply filters and sort with new sort order
    this.applyFiltersAndSort();
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((o, p) => o?.[p], obj) ?? '';
  }

  // Step 2: Action methods
  onBulkActionChange(): void {
    if (!this.bulkAction) {
      return;
    }

    if (this.applyToScope === 'all') {
      this.applyActionToAll(this.bulkAction);
    } else {
      this.applyActionToFiltered(this.bulkAction);
    }
    this.updateHeaderCheckboxStates();
  }

  private applyActionToAll(action: MergeAction): void {
    for (const change of this.allMergeChanges) {
      change.action = action;
    }
    for (const change of this.filteredMergeChanges) {
      change.action = action;
    }
    for (const change of this.mergeChanges) {
      change.action = action;
    }
  }

  private applyActionToFiltered(action: MergeAction): void {
    for (const change of this.filteredMergeChanges) {
      change.action = action;
    }
    for (const change of this.mergeChanges) {
      change.action = action;
    }
    const filteredIds = new Set<string>(this.filteredMergeChanges.map((c) => c.id));
    for (const change of this.allMergeChanges) {
      if (filteredIds.has(change.id)) {
        change.action = action;
      }
    }
  }

  onActionChange(change: MergeChange): void {
    // The change object should already be updated via ngModel binding
    // But we also need to ensure allMergeChanges is updated for the getBlockingConflictCount check
    const originalChange = this.allMergeChanges.find((c) => c.id === change.id);
    if (originalChange && originalChange !== change) {
      originalChange.action = change.action;
    }
    // Also update in filteredMergeChanges if different reference
    const filteredChange = this.filteredMergeChanges.find((c) => c.id === change.id);
    if (filteredChange && filteredChange !== change) {
      filteredChange.action = change.action;
    }
    // Reset bulk action when individual action changes
    this.bulkAction = null;
    // Update header checkbox states based on current selections
    this.updateHeaderCheckboxStates();
  }

  /**
   * Select all changes to Pick Source when header checkbox is checked
   * Only applies to filtered/displayed changes
   */
  onSelectAllSource(): void {
    this.applyBulkActionWithConfirmation(
      'MERGE_SOURCE',
      'selectAllSource',
      'CONFIRM_SELECT_SOURCE_ALL',
      this.sourceBranch?.refsName
    );
  }

  /**
   * Select all changes to Pick Target when header checkbox is checked
   * Only applies to filtered/displayed changes
   */
  onSelectAllTarget(): void {
    this.applyBulkActionWithConfirmation(
      'MERGE_TARGET',
      'selectAllTarget',
      'CONFIRM_SELECT_TARGET_ALL',
      this.targetBranch?.refsName
    );
  }

  /**
   * Select all changes to Don't Merge when header checkbox is checked
   * Only applies to filtered/displayed changes
   */
  onSelectAllDontMerge(): void {
    this.applyBulkActionWithConfirmation('MERGE_LATER', 'selectAllDontMerge', 'CONFIRM_SELECT_MERGE_LATER_ALL', '');
  }

  /**
   * Common utility method to apply bulk action with confirmation dialog
   * @param action The merge action to apply
   * @param checkboxProperty The property name of the checkbox that triggered this action
   * @param confirmMessageKey The translation key for the confirmation message
   */
  private applyBulkActionWithConfirmation(
    action: MergeAction,
    checkboxProperty: 'selectAllSource' | 'selectAllTarget' | 'selectAllDontMerge',
    confirmMessageKey: string,
    branchName?: string
  ): void {
    if (!this[checkboxProperty]) {
      return;
    }

    this.confirmationDialogService.confirm({
      message: this.translate.instant(confirmMessageKey, { branchName: branchName || '' }),
      header: this.translate.instant('CONFIRM_ACTION'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      accept: () => {
        // Reset all checkboxes, then set the current one
        this.selectAllSource = false;
        this.selectAllTarget = false;
        this.selectAllDontMerge = false;
        this[checkboxProperty] = true;

        // Apply action to all filtered changes
        for (const change of this.filteredMergeChanges) {
          change.action = action;
        }
        this.applyFiltersAndSort();
      },
      reject: () => {
        // Revert checkbox state
        this[checkboxProperty] = false;
      }
    });
  }

  /**
   * Update header checkbox states based on current action selections
   * Only considers filtered/displayed changes
   */
  private updateHeaderCheckboxStates(): void {
    if (this.filteredMergeChanges.length === 0) {
      this.selectAllSource = false;
      this.selectAllTarget = false;
      this.selectAllDontMerge = false;
      return;
    }
    const allSource = this.filteredMergeChanges.every((c) => c.action === 'MERGE_SOURCE');
    const allTarget = this.filteredMergeChanges.every((c) => c.action === 'MERGE_TARGET');
    const allDontMerge = this.filteredMergeChanges.every((c) => c.action === 'MERGE_LATER');
    this.selectAllSource = allSource;
    this.selectAllTarget = allTarget;
    this.selectAllDontMerge = allDontMerge;
  }

  // Step 2: Change type styling
  getChangeTypeClass(changeType: RecordChangeType): string {
    const classMap: Record<RecordChangeType, string> = {
      ADDED: 'change-added',
      MODIFIED: 'change-modified',
      RENAMED: 'change-renamed',
      DELETED: 'change-deleted'
    };
    return classMap[changeType] || '';
  }

  /**
   * Returns the display label for a change type.
   * If additionalRecordChangeTypeList contains both RENAMED and MODIFIED, show "Renamed & Modified".
   */
  getChangeTypeDisplayLabel(change: MergeChange): string {
    const additionalTypes = change.additionalRecordChangeTypeList || [];
    if (additionalTypes.includes('RENAMED') && additionalTypes.includes('MODIFIED')) {
      return this.translate.instant('RENAMED_AND_MODIFIED');
    }
    return change.recordChangeType;
  }

  // Step 2: Count methods - use allMergeChanges for accurate total counts
  getChangeCount(changeType: RecordChangeType): number {
    return this.allMergeChanges.filter((c) => c.recordChangeType === changeType).length;
  }

  getErrorCount(): number {
    return this.allMergeChanges.filter((c) => c.hasError || c.hasConflict).length;
  }

  /**
   * Get human-readable conflict messages for a change (internationalized)
   * @param change The merge change with conflicts
   * @returns A formatted string with all conflict messages
   */
  getConflictMessages(change: MergeChange | ReviewMergeChange): string {
    if (!change.conflicts || change.conflicts.length === 0) {
      return this.translate.instant('CONFLICT_DETECTED');
    }

    return change.conflicts
      .map((conflict) => {
        const messageKey = CONFLICT_MESSAGE_KEYS[conflict];
        return messageKey ? this.translate.instant(messageKey) : conflict;
      })
      .join('\n');
  }

  /**
   * Get count of conflicts that block proceeding to next step.
   * User can proceed if conflicted change action is NOT "Pick Source".
   * This means "Don't Merge" or "Pick Target" are allowed for conflicted changes.
   */
  getBlockingConflictCount(): number {
    return this.allMergeChanges.filter((c) => (c.hasError || c.hasConflict) && c.action === 'MERGE_SOURCE').length;
  }

  /**
   * Check if there are any conflicts in the review changes (Step 3)
   * that would block the merge operation.
   */
  hasReviewConflicts(): boolean {
    return this.reviewChanges.some((c) => c.hasConflict || c.hasError);
  }

  /**
   * Get tooltip message for merge button based on current state
   */
  getMergeButtonTooltip(): string {
    if (this.hasReviewConflicts()) {
      return this.translate.instant('CANNOT_MERGE_CONFLICTS');
    }
    if (this.isValidatingTag) {
      return this.translate.instant('VALIDATING_TAG_NAME');
    }
    if (!this.tagName?.trim()) {
      return this.translate.instant('TAG_NAME_REQUIRED');
    }
    if (this.tagValidationError) {
      return this.tagValidationError;
    }
    return '';
  }

  getActionCount(action: MergeAction): number {
    return this.allMergeChanges.filter((c) => c.action === action).length;
  }

  // Get total count of all changes (for display)
  get totalChangesCount(): number {
    return this.allMergeChanges.length;
  }

  // Get filtered count (for display when filters are active)
  get filteredChangesCount(): number {
    return this.filteredMergeChanges.length;
  }

  // Check if filters are active
  get hasActiveFilters(): boolean {
    return (
      this.filterApplications?.length > 0 ||
      this.filterComponentTypes?.length > 0 ||
      this.filterChangeTypes?.length > 0 ||
      this.filterActionTypes?.length > 0 ||
      this.filterModifiedBy?.length > 0 ||
      this.filterConflicts?.length > 0
    );
  }

  // Step 3: Review counts
  get reviewTotalCount(): number {
    return this.filteredReviewChanges.filter((c) => c.componentType !== 'RUNTIME_CONFIGURATION').length;
  }

  get reviewUserSelectedCount(): number {
    return this.filteredReviewChanges.filter((c) => !c.isAutoSelected && c.componentType !== 'RUNTIME_CONFIGURATION').length;
  }

  get reviewAutoSelectedCount(): number {
    return this.filteredReviewChanges.filter((c) => c.isAutoSelected && c.componentType !== 'RUNTIME_CONFIGURATION').length;
  }

  // Step 3: Filter methods
  /**
   * Initialize review component type options from loaded review changes
   */
  initReviewComponentTypeOptions(): void {
    const uniqueTypes = [...new Set<string>(this.reviewChanges.map((c) => c.componentType))].filter((t) => t !== 'RUNTIME_CONFIGURATION');
    this.reviewComponentTypeOptions = uniqueTypes
      .map((type) => ({
        label: this.utilService.formatComponentType(type),
        value: type
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Build grouped review changes - manual components with their auto-selected children
   */
  buildGroupedReviewChanges(): void {
    this.buildGroupedReviewChangesFromSource(this.reviewChanges);
  }

  /**
   * Build grouped review changes from filtered results
   */
  buildGroupedReviewChangesFromFiltered(): void {
    this.buildGroupedReviewChangesFromSource(this.filteredReviewChanges);
  }

  /**
   * Build grouped review changes from a source array
   */
  private buildGroupedReviewChangesFromSource(sourceChanges: ReviewMergeChange[]): void {
    // Hide RUNTIME_CONFIGURATION from UI display (still included in merge payload)
    const visible = sourceChanges.filter((c) => c.componentType !== 'RUNTIME_CONFIGURATION');
    const manualChanges = visible.filter((c) => !c.isAutoSelected);
    const autoChanges = visible.filter((c) => c.isAutoSelected);

    const manualMap = new Map<string, ReviewMergeChange>();
    for (const c of manualChanges) {
      manualMap.set(c.id, c);
    }

    const allChangesMap = new Map<string, ReviewMergeChange>();
    for (const c of visible) {
      allChangesMap.set(c.id, c);
    }

    const childToDirectParentMap = this.buildChildToParentMap(visible);
    const childrenMap = new Map<string, ReviewMergeChange[]>();
    const usedAsChild = new Set<string>();

    this.assignAutoToDirectAncestors(
      autoChanges,
      childToDirectParentMap,
      allChangesMap,
      manualMap,
      childrenMap,
      usedAsChild
    );
    this.assignUnassignedViaGraph(
      autoChanges,
      usedAsChild,
      manualChanges,
      visible,
      allChangesMap,
      manualMap,
      childrenMap
    );

    this.groupedReviewChanges = manualChanges.map((parent) => ({
      parent,
      children: childrenMap.get(parent.id) || [],
      expanded: this.expandedGroups.has(parent.id)
    }));

    for (const autoChange of autoChanges) {
      if (!usedAsChild.has(autoChange.id)) {
        this.groupedReviewChanges.push({ parent: autoChange, children: [], expanded: false });
      }
    }
  }

  private buildChildToParentMap(sourceChanges: ReviewMergeChange[]): Map<string, string> {
    const map = new Map<string, string>();
    for (const change of sourceChanges) {
      if (!change.dependentComponentIds?.length) {
        continue;
      }
      for (const childKey of change.dependentComponentIds) {
        if (!map.has(childKey)) {
          map.set(childKey, change.id);
        }
      }
    }
    return map;
  }

  private findManualAncestor(
    autoChangeId: string,
    childToDirectParentMap: Map<string, string>,
    allChangesMap: Map<string, ReviewMergeChange>,
    visited: Set<string> = new Set<string>()
  ): string | null {
    if (visited.has(autoChangeId)) {
      return null;
    }
    visited.add(autoChangeId);

    const directParentKey = childToDirectParentMap.get(autoChangeId);
    if (!directParentKey || !allChangesMap.has(directParentKey)) {
      return null;
    }

    const parentChange = allChangesMap.get(directParentKey)!;
    if (!parentChange.isAutoSelected) {
      return directParentKey;
    }

    return this.findManualAncestor(directParentKey, childToDirectParentMap, allChangesMap, visited);
  }

  private assignAutoToDirectAncestors(
    autoChanges: ReviewMergeChange[],
    childToDirectParentMap: Map<string, string>,
    allChangesMap: Map<string, ReviewMergeChange>,
    manualMap: Map<string, ReviewMergeChange>,
    childrenMap: Map<string, ReviewMergeChange[]>,
    usedAsChild: Set<string>
  ): void {
    for (const autoChange of autoChanges) {
      const ancestorKey = this.findManualAncestor(autoChange.id, childToDirectParentMap, allChangesMap);
      if (!ancestorKey || !manualMap.has(ancestorKey)) {
        continue;
      }
      if (!childrenMap.has(ancestorKey)) {
        childrenMap.set(ancestorKey, []);
      }
      childrenMap.get(ancestorKey)!.push(autoChange);
      usedAsChild.add(autoChange.id);
    }
  }

  private assignUnassignedViaGraph(
    autoChanges: ReviewMergeChange[],
    usedAsChild: Set<string>,
    manualChanges: ReviewMergeChange[],
    sourceChanges: ReviewMergeChange[],
    allChangesMap: Map<string, ReviewMergeChange>,
    manualMap: Map<string, ReviewMergeChange>,
    childrenMap: Map<string, ReviewMergeChange[]>
  ): void {
    const unassigned = autoChanges.filter((ac) => !usedAsChild.has(ac.id));
    if (unassigned.length === 0 || manualChanges.length === 0) {
      return;
    }

    const adjacency = this.buildUndirectedAdjacency(sourceChanges, allChangesMap);

    for (const autoChange of unassigned) {
      const foundManual = this.bfsFindManualParent(autoChange.id, adjacency, manualMap);
      if (!foundManual) {
        continue;
      }
      if (!childrenMap.has(foundManual)) {
        childrenMap.set(foundManual, []);
      }
      childrenMap.get(foundManual)!.push(autoChange);
      usedAsChild.add(autoChange.id);
    }
  }

  private buildUndirectedAdjacency(
    sourceChanges: ReviewMergeChange[],
    allChangesMap: Map<string, ReviewMergeChange>
  ): Map<string, Set<string>> {
    const adj = new Map<string, Set<string>>();
    for (const change of sourceChanges) {
      if (!change.dependentComponentIds?.length) {
        continue;
      }
      for (const childKey of change.dependentComponentIds) {
        if (!allChangesMap.has(childKey)) {
          continue;
        }
        if (!adj.has(change.id)) {
          adj.set(change.id, new Set<string>());
        }
        if (!adj.has(childKey)) {
          adj.set(childKey, new Set<string>());
        }
        adj.get(change.id)!.add(childKey);
        adj.get(childKey)!.add(change.id);
      }
    }
    return adj;
  }

  private bfsFindManualParent(
    startId: string,
    adjacency: Map<string, Set<string>>,
    manualMap: Map<string, ReviewMergeChange>
  ): string | null {
    const visited = new Set<string>();
    const queue = [startId];
    visited.add(startId);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const neighbors = adjacency.get(current);
      if (!neighbors) continue;

      for (const neighbor of neighbors) {
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        if (manualMap.has(neighbor)) return neighbor;
        queue.push(neighbor);
      }
    }

    return null;
  }

  /**
   * Toggle expansion of a grouped row
   */
  toggleGroupExpansion(group: GroupedReviewChange): void {
    if (group.children.length === 0) return;

    const newExpanded = !group.expanded;
    if (newExpanded) {
      this.expandedGroups.add(group.parent.id); // Use composite key (id) to handle same componentId across types
    } else {
      this.expandedGroups.delete(group.parent.id);
    }

    // Reassign array to trigger change detection
    // Use composite key (id) for comparison to correctly identify groups
    this.groupedReviewChanges = this.groupedReviewChanges.map((g) =>
      g.parent.id === group.parent.id ? { ...g, expanded: newExpanded } : g
    );
  }

  /**
   * Expand all groups
   */
  expandAllGroups(): void {
    this.groupedReviewChanges = this.groupedReviewChanges.map((group) => {
      if (group.children.length > 0) {
        this.expandedGroups.add(group.parent.id); // Use composite key (id) to handle same componentId across types
        return { ...group, expanded: true };
      }
      return group;
    });
  }

  /**
   * Collapse all groups
   */
  collapseAllGroups(): void {
    this.groupedReviewChanges = this.groupedReviewChanges.map((group) => ({
      ...group,
      expanded: false
    }));
    this.expandedGroups.clear();
  }

  /**
   * Check if any child in the group has a conflict
   */
  hasChildConflict(group: GroupedReviewChange): boolean {
    return group.children.some((child) => child.hasConflict);
  }

  /**
   * Get "required by" names for an auto-selected component (for tooltip when shared by multiple parents)
   */
  getRequiredByNames(change: ReviewMergeChange): string {
    if (!change.dependentComponentNames || change.dependentComponentNames.length <= 1) {
      return '';
    }
    return this.translate.instant('ALSO_REQUIRED_BY') + ': ' + change.dependentComponentNames.slice(1).join(', ');
  }

  /**
   * Apply filters to review changes
   */
  applyReviewFilters(): void {
    let result = [...this.reviewChanges];

    // Apply global search
    if (this.reviewGlobalSearch?.trim()) {
      const searchTerm = this.reviewGlobalSearch.toLowerCase().trim();
      result = result.filter(
        (change) =>
          change.sourceComponentName?.toLowerCase().includes(searchTerm) ||
          change.targetComponentName?.toLowerCase().includes(searchTerm) ||
          change.componentType?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply application filter
    if (this.isSuiteMerge && this.reviewFilterApplications?.length > 0) {
      result = result.filter((change) => {
        const appName = (change as any).applicationName as string | undefined;
        return appName && this.reviewFilterApplications.includes(appName);
      });
    }

    // Apply component type filter
    if (this.reviewFilterComponentTypes?.length > 0) {
      result = result.filter((change) => this.reviewFilterComponentTypes.includes(change.componentType));
    }

    // Apply change type filter
    if (this.reviewFilterChangeTypes?.length > 0) {
      result = result.filter((change) => {
        const additionalTypes = change.additionalRecordChangeTypeList || [];
        const isRenamedAndModified = additionalTypes.includes('RENAMED') && additionalTypes.includes('MODIFIED');
        return this.reviewFilterChangeTypes.some((filterType) => {
          if (filterType === 'RENAMED_AND_MODIFIED') {
            return isRenamedAndModified;
          }
          return change.recordChangeType === filterType && !isRenamedAndModified;
        });
      });
    }

    this.filteredReviewChanges = result;

    // Rebuild grouped changes from filtered results
    this.buildGroupedReviewChangesFromFiltered();
  }

  /**
   * Open review filter dialog
   */
  openReviewFilterDialog(): void {
    this.tempReviewFilterComponentTypes = [...this.reviewFilterComponentTypes];
    this.tempReviewFilterChangeTypes = [...this.reviewFilterChangeTypes];
    this.showReviewFilterDialog = true;
  }

  /**
   * Apply review filters from dialog
   */
  applyReviewFilterDialog(): void {
    this.reviewFilterComponentTypes = [...this.tempReviewFilterComponentTypes];
    this.reviewFilterChangeTypes = [...this.tempReviewFilterChangeTypes];
    this.showReviewFilterDialog = false;
    this.applyReviewFilters();
  }

  /**
   * Cancel review filter dialog
   */
  cancelReviewFilterDialog(): void {
    this.showReviewFilterDialog = false;
  }

  /**
   * Clear all review filters in dialog
   */
  clearReviewFilterDialog(): void {
    this.tempReviewFilterComponentTypes = [];
    this.tempReviewFilterChangeTypes = [];
  }

  /**
   * Called when review filter popover is shown - initialize temp values
   */
  onReviewFilterPopoverShow(): void {
    this.tempReviewFilterComponentTypes = [...this.reviewFilterComponentTypes];
    this.tempReviewFilterChangeTypes = [...this.reviewFilterChangeTypes];
  }

  /**
   * Apply review filters from popover
   */
  applyReviewFilterPopover(popover: any): void {
    this.reviewFilterComponentTypes = [...this.tempReviewFilterComponentTypes];
    this.reviewFilterChangeTypes = [...this.tempReviewFilterChangeTypes];
    popover.hide();
    this.applyReviewFilters();
  }

  /**
   * Clear all review filters in popover
   */
  clearReviewFilterPopover(): void {
    this.tempReviewFilterComponentTypes = [];
    this.tempReviewFilterChangeTypes = [];
  }

  /**
   * Clear all review filters
   */
  clearReviewFilters(): void {
    this.reviewFilterApplications = [];
    this.reviewFilterComponentTypes = [];
    this.reviewFilterChangeTypes = [];
    this.reviewGlobalSearch = '';
    this.applyReviewFilters();
  }

  /**
   * Called when any review filter dropdown changes directly
   */
  onReviewFilterChange(): void {
    this.applyReviewFilters();
  }

  /**
   * Remove a specific review filter chip
   */
  removeReviewFilter(type: 'component' | 'change', value: string): void {
    if (type === 'component') {
      this.reviewFilterComponentTypes = this.reviewFilterComponentTypes.filter((v) => v !== value);
    } else if (type === 'change') {
      this.reviewFilterChangeTypes = this.reviewFilterChangeTypes.filter((v) => v !== value);
    }
    this.applyReviewFilters();
  }

  /**
   * Get label for a review filter value
   */
  getReviewFilterLabel(type: 'component' | 'change', value: string): string {
    if (type === 'component') {
      return this.reviewComponentTypeOptions.find((o) => o.value === value)?.label || this.utilService.formatComponentType(value);
    } else {
      return this.changeTypeOptions.find((o) => o.value === value)?.label || value;
    }
  }

  /**
   * Get total active review filter count
   */
  get activeReviewFilterCount(): number {
    return (
      (this.reviewFilterApplications?.length || 0) +
      (this.reviewFilterComponentTypes?.length || 0) +
      (this.reviewFilterChangeTypes?.length || 0)
    );
  }

  /**
   * Check if review filters are active
   */
  get hasActiveReviewFilters(): boolean {
    return (
      this.reviewFilterApplications?.length > 0 ||
      this.reviewFilterComponentTypes?.length > 0 ||
      this.reviewFilterChangeTypes?.length > 0 ||
      !!this.reviewGlobalSearch?.trim()
    );
  }

  /**
   * Get display label for action
   */
  getActionLabel(action: string): string {
    const actionMap: Record<string, string> = {
      MERGE_SOURCE: this.sourceBranch?.refsName || this.translate.instant('MERGE_SOURCE'),
      MERGE_TARGET: this.targetBranch?.refsName || this.translate.instant('MERGE_TARGET'),
      MERGE_LATER: this.translate.instant('MERGE_LATER')
    };
    return actionMap[action] || action;
  }

  /**
   * Get the type label from a path string (e.g., "Table" from "Table: tablename")
   */
  getPathTypeLabel(path: string | undefined): string {
    if (!path) return '';
    const colonIndex = path.indexOf(':');
    return colonIndex > 0 ? path.substring(0, colonIndex).trim() : '';
  }

  /**
   * Get the name from a path string (e.g., "tablename" from "Table: tablename")
   */
  getPathName(path: string | undefined): string {
    if (!path) return '';
    const colonIndex = path.indexOf(':');
    return colonIndex > 0 ? path.substring(colonIndex + 1).trim() : path;
  }

  private aggregateGroupDependentComponents(
    groupComponents: { compositeKey: string; componentId: string; detail: ComponentDetail }[],
    groupId: string,
    allComponentsMap: Map<string, { compositeKey: string; componentId: string; detail: ComponentDetail }>,
    groupedComponentIds: string[]
  ): { ids: string[]; data: { id: string; compositeKey: string; type: string }[] } {
    const groupedIdSet = new Set(groupedComponentIds);
    const data: { id: string; compositeKey: string; type: string }[] = [];
    const seenCompositeKeys = new Set<string>();

    const allSources = [...groupComponents];
    const parentComponent = allComponentsMap.get(groupId);
    if (parentComponent && !allSources.some((c) => c.componentId === groupId)) {
      allSources.push(parentComponent);
    }

    for (const c of allSources) {
      this.collectDependentEntries(c.detail, groupedIdSet, seenCompositeKeys, data);
    }

    return { ids: data.map((d) => d.compositeKey), data };
  }

  private collectDependentEntries(
    detail: ComponentDetail,
    groupedIdSet: Set<string>,
    seenCompositeKeys: Set<string>,
    data: { id: string; compositeKey: string; type: string }[]
  ): void {
    if (!detail.dependentComponents) {
      return;
    }

    for (const [compType, compIds] of Object.entries(detail.dependentComponents)) {
      if (!Array.isArray(compIds)) {
        continue;
      }
      for (const id of compIds as string[]) {
        if (groupedIdSet.has(id)) {
          continue;
        }
        const compositeKey = `${compType}_${id}`;
        if (seenCompositeKeys.has(compositeKey)) {
          continue;
        }
        seenCompositeKeys.add(compositeKey);
        data.push({ id, compositeKey, type: this.utilService.formatComponentType(compType) });
      }
    }
  }

  /**
   * Resolve a display name from appMetaInfo for a given model ID.
   * Falls back to empty string when no usable name exists.
   */
  private getAppMetaDisplayName(modelId: string | undefined): string {
    if (!modelId) return '';

    const appMetaInfo = this.appGlobalService.get('appmetaInfo') || {};
    const metaValue = appMetaInfo[modelId];

    if (typeof metaValue === 'string') {
      return metaValue;
    }

    if (metaValue && typeof metaValue === 'object') {
      return metaValue.name || metaValue.displayName || metaValue.label || '';
    }

    return '';
  }

  // Avatar color generation
  getAvatarColor(name: string): string {
    if (!name) return '#6c757d';

    if (this.avatarColorCache.has(name)) {
      return this.avatarColorCache.get(name)!;
    }

    const color = this.repoService.getBgColor(name, 55, 42);
    this.avatarColorCache.set(name, color);
    return color;
  }

  private getModelTypeForMerge(componentType: string, actionType: string): string {
    const FIELD_ACTIONS = ['MANDATORY_FIELD_REMOVE', 'OPTIONAL_FIELD_REMOVE', 'UNIQUE_FIELD_REMOVE'];
    const WORKFLOW_ACTOR_ACTIONS = [
      'WORKFLOW_ACTOR_DELETE',
      'REMOVE_FIELD_FROM_WORKFLOW_ACTOR_ENABLED_FIELDS',
      'REMOVE_FIELD_FROM_WORKFLOW_ACTOR_HIDDEN_FIELDS',
      'REMOVE_FIELD_FROM_WORKFLOW_ACTOR_SHOW_FIELDS',
      'REMOVE_FIELD_FROM_WORKFLOW_ACTOR_DISABLED_FIELDS',
      'REMOVE_ACTION_FROM_WORKFLOW_ACTOR_DISABLED_ACTION',
      'REMOVE_FIELD_FROM_WORKFLOW_FROM_ACTOR',
      'REMOVE_ACTION_FROM_WORKFLOW_ACTOR_ENABLED_ACTION',
      'REMOVE_ACTION_FROM_WORKFLOW_ACTOR_HIDDEN_ACTION',
      'REMOVE_ACTION_FROM_WORKFLOW_ACTOR_SHOW_ACTION',
      'REMOVE_ROLE_FROM_WORKFLOW_FROM_ACTOR'
    ];
    const WORKFLOW_ACTION_ACTIONS = [
      'WORKFLOW_ACTION_DELETE',
      'REMOVE_FROM_WORKFLOW_ACTION',
      'REMOVE_BUTTON_FROM_PAGE',
      'REMOVE_ROLE_FROM_WORKFLOW_ACTION_NEXT_ACTOR',
      'WORKFLOW_ACTION_NEXT_STATUS_UPDATE',
      'WORKFLOW_ACTION_RENAME',
      'REMOVE_FIELD_FROM_WORKFLOW_ACTION_MANDATORY_FIELDS',
      'REMOVE_FIELD_FROM_WORKFLOW_NEXT_ACTOR'
    ];
    if (componentType === 'TABLE' && FIELD_ACTIONS.includes(actionType)) {
      return 'FIELD';
    }
    if (componentType === 'WORKFLOW' && WORKFLOW_ACTOR_ACTIONS.includes(actionType)) {
      return 'WORKFLOW_ACTOR';
    }
    if (componentType === 'WORKFLOW' && WORKFLOW_ACTION_ACTIONS.includes(actionType)) {
      return 'WORKFLOW_ACTION';
    }
    return componentType;
  }

  /**
   * Get user display name from appMetaInfo using user SID
   * Falls back to the original value if not found
   */
  getUserDisplayName(userSid: string): string {
    if (!userSid) return '';
    const appMetaInfo = this.appGlobalService.get('appmetaInfo') || {};
    return appMetaInfo[userSid] || userSid;
  }

  /**
   * Check if a component has modelActionTypes (references to display)
   */
  hasModelActionTypes(change: ReviewMergeChange): boolean {
    return (
      (change.componentType === 'APP' && change.recordChangeType === 'DELETED') ||
      (change.componentType === 'TECHNOLOGY_CHOICES' && change.recordChangeType === 'MODIFIED') ||
      !!(change.modelActionTypes && Object.keys(change.modelActionTypes).length > 0)
    );
  }

  setAppVersion(appId: string) {
    this.repoService.getAppWithOutMenu(appId).subscribe({
      next: (app) => {
        if (app?.rappitVersion) {
          this.appGlobalService.write('appVersion', app.rappitVersion);
        } else {
          this.utilService.showMessage('error', this.translate.instant('FAILED_TO_FETCH_APP_VERSION'));
        }
      },
      error: () => {
        this.utilService.showMessage('error', this.translate.instant('FAILED_TO_FETCH_APP_VERSION'));
      }
    });
  }

  /**
   * View references for a component with modelActionTypes
   * Uses ReferenceUtilService to fetch references and display the result.
   */
  async viewReferences(change: ReviewMergeChange, event: Event): Promise<void> {
    event.stopPropagation(); // Prevent row expansion toggle
    const actionMap: Record<string, string> = {
      DELETED: 'DELETE',
      MODIFIED: 'UPDATE',
      ADDED: 'UPDATE',
      RENAMED: 'RENAME'
    };
    const action = actionMap[change.recordChangeType] || 'UPDATE';
    let payload: ModelReferencePayload | ModelReferencePayload[];

    if (change.componentType === 'APP' && change.recordChangeType === 'DELETED') {
      if (!change.componentId) {
        return;
      }
      payload = {
        modelId: change.componentId,
        appId: change.targetAppId || '',
        targetMemberApp: true,
        modelType: 'APP',
        actionType: action,
        action
      };
    } else if (change.componentType === 'TECHNOLOGY_CHOICES' && change.recordChangeType === 'MODIFIED') {
      const removedDatasetsStr = change.additionalInfo?.removedDatasets;

      if (removedDatasetsStr) {
        // Split comma-separated string and trim any extra spaces
        const datasetIds = removedDatasetsStr
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        // Map over each dataset ID to create an array of payloads
        payload = datasetIds.map((datasetId) => ({
          modelId: datasetId,
          modelType: 'BQ_DATASET',
          actionType: 'DATA_SET_DELETE',
          action,
          appId: this.targetBranch!.sid
        }));
      } else {
        // Fallback if no datasets were found in additionalInfo
        this.utilService.showMessage('info', this.translate.instant('NO_TARGET_BRANCH_REFERENCES_FOUND'));
        return;
      }
    } else {
      if (!change.modelActionTypes) {
        return;
      }
      payload = Object.entries(change.modelActionTypes).map(([modelId, actionType]) => ({
        modelId,
        modelType: this.getModelTypeForMerge(change.componentType, actionType),
        actionType,
        action,
        appId: change.targetAppId || this.targetBranch?.sid
      }));
    }

    if (Array.isArray(payload) && payload.length === 0) {
      return;
    }
    this.setAppVersion(this.targetBranch?.sid || '');
    await this.referenceUtilService.handleWithReferences({
      payload,
      node: {
        name: change.sourceComponentName || change.targetComponentName || change.componentId,
        type: change.componentType
      },
      onConfirm: () => {
        // Do nothing on confirm
      },
      onNoReferences: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('NO_REFERENCES'),
          detail: this.translate.instant('NO_TARGET_BRANCH_REFERENCES_FOUND')
        });
      },
      onCancel: () => {
        // Do nothing on cancel
      },
      onError: (err: unknown) => {
        console.error('Error fetching references:', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('ERROR'),
          detail: this.translate.instant('FAILED_TO_FETCH_REFERENCES')
        });
      },
      filter: (refs: unknown[]) => refs.filter((ref) => ref && typeof ref === 'object' && Object.keys(ref).length > 0),
      extras: {
        hideConfirm: true
      }
    });
  }
  private checkForUnpublishedChanges(appId: string, branchType: 'source' | 'target'): void {
    if (!appId) {
      if (branchType === 'source') {
        this.unPublishedSource = false;
      } else {
        this.unPublishedTarget = false;
      }
      return;
    }

    this.repoService.checkForUnpublishedChanges(appId).subscribe({
      next: (hasUnpublishedChanges: unknown) => {
        const hasChanges = Boolean(hasUnpublishedChanges);
        if (branchType === 'source') {
          this.unPublishedSource = hasChanges;
        } else {
          this.unPublishedTarget = hasChanges;
        }
      },
      error: () => {
        if (branchType === 'source') {
          this.unPublishedSource = false;
        } else {
          this.unPublishedTarget = false;
        }
      }
    });
  }
}
